<template>
  <main class="relative isolate min-h-screen">
    <HeroCanvas />
    <div class="gradient-overlay"></div>

    <header class="site-header">
      <div class="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-8" style="margin: 0 auto">
        <a
          href="/"
          class="brand-link"
          aria-label="VitaeAI 首页"
        >
          <img class="brand-mark" src="/logos/vitaeai-mark.svg" alt="" aria-hidden="true" />
          <img class="brand-logo" src="/logos/vitaeai-transparent.png" alt="VitaeAI" />
        </a>

        <nav class="hidden items-center gap-10 md:flex">
          <a
            v-for="item in navItems"
            :key="item.view"
            :href="item.path"
            :class="{ active: activeView === item.view }"
            class="nav-link"
            @click.prevent="goToPath(item.path)"
          >
            {{ item.label }}
          </a>
        </nav>

        <div class="header-actions">
          <a
            href="/user"
            class="header-cta header-cta-ghost"
            :class="{ active: activeView === 'user-center' }"
            @click.prevent="openUserCenter"
          >
<!--            @click.prevent="showDevelopingDialog"-->
            用户中心
          </a>
          <a
            href="https://agent.vitaeai.cn/web/login"
            class="header-cta header-cta-ghost"
            target="_blank"
            rel="noreferrer"
          >
            控制台
          </a>
          <a
            v-if="!isLoggedIn"
            href="/register"
            class="header-cta header-cta-ghost"
            :class="{ active: activeView === 'register' }"
            @click.prevent="goToPath('/register')"
          >
            注册账号
          </a>
          <a
            href="/"
            class="header-cta"
            @click.prevent="goToPath('/')"
          >
            查看产品
          </a>
        </div>

        <button
          class="mobile-menu-toggle flex flex-col p-1.5 md:hidden"
          :class="{ 'mobile-menu-open': mobileMenuOpen }"
          aria-label="Toggle menu"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>
      </div>

      <Transition name="slide-down">
        <nav
          v-if="mobileMenuOpen"
          class="flex flex-col gap-3 border-t border-white/[0.06] bg-[#030914]/95 px-6 pb-6 pt-4 backdrop-blur-xl md:hidden"
        >
          <a
            v-for="item in navItems"
            :key="item.view"
            :href="item.path"
            class="mobile-nav-link py-2 text-sm font-semibold text-white/68 transition-colors hover:text-white"
            @click.prevent="mobileMenuOpen = false; goToPath(item.path)"
          >
            {{ item.label }}
          </a>
          <a
            href="/user"
            class="mobile-nav-link py-2 text-sm font-semibold text-white/68 transition-colors hover:text-white"
            @click.prevent="mobileMenuOpen = false; openUserCenter()"
          >
<!--            @click.prevent="mobileMenuOpen = false; showDevelopingDialog()"-->

            用户中心
          </a>
          <a
            href="https://agent.vitaeai.cn/web/login"
            class="mobile-nav-link py-2 text-sm font-semibold text-white/68 transition-colors hover:text-white"
            target="_blank"
            rel="noreferrer"
            @click="mobileMenuOpen = false"
          >
            控制台
          </a>
          <a
            v-if="!isLoggedIn"
            href="/register"
            class="mobile-nav-link py-2 text-sm font-semibold text-white/68 transition-colors hover:text-white"
            @click.prevent="mobileMenuOpen = false; goToPath('/register')"
          >
            注册账号
          </a>
        </nav>
      </Transition>
    </header>

    <div v-if="activeView === 'home'" class="floating-contact" aria-label="客服联系方式">
      <button class="floating-contact-qr" type="button" aria-label="预览客服微信二维码" @click="openQrPreview">
        <img
          v-if="productConfig.customerServiceQrCode"
          class="wechat-qr-image"
          :src="productConfig.customerServiceQrCode"
          alt="客服微信二维码"
        />
        <span v-else class="wechat-qr" aria-label="客服微信二维码">
          <span v-for="cell in wechatQrCells" :key="cell" :style="{ gridArea: cell }"></span>
        </span>
      </button>
      <div class="floating-contact-copy">
        <span>客服支持</span>
        <strong>微信咨询</strong>
        <button type="button" @click="copyContactEmail">
          {{ contactEmail }}
          <em>{{ emailCopyText }}</em>
        </button>
      </div>
    </div>

    <section v-if="activeView === 'home'" class="relative">
      <div class="home-hero">
        <div class="hero-copy">
          <p class="section-eyebrow">{{ company.brand }} · AI COSMOS</p>
          <h2 class="hero-title">
            重构人与<br />
            数字世界的交互方式
          </h2>
          <p class="hero-subtitle">
<!--            连接用户、品牌、内容与商业服务，构建下一代 AI-native 数字商业生态。-->
            每一次点击、每一次对话、每一次指令，都是人与数字世界发生关系的契机。
          </p>
          <div class="hero-actions">
            <a href="#product-employee" class="primary-cta" @click.prevent="scrollToProduct('product-employee')">
              VitaeAI 智能体
            </a>
            <a href="/download" class="secondary-cta" @click.prevent="goToPath('/download')">
              下载点指之间
            </a>
          </div>
        </div>

        <div class="orbital-panel" aria-label="AI 数字生态示意图" role="img">
          <div class="orbit orbit-one"></div>
          <div class="orbit orbit-two"></div>
          <div class="orbit-core">AI</div>
          <div class="satellite satellite-one">用户</div>
          <div class="satellite satellite-two">品牌</div>
          <div class="satellite satellite-three">内容</div>
          <div class="satellite satellite-four">服务</div>
        </div>
      </div>

      <div class="home-strategy">
        <div class="strategy-grid">
          <article v-for="section in homeSections" :key="section.label" class="strategy-card">
            <p>{{ section.label }}</p>
            <h2>{{ section.title }}</h2>
            <span>{{ section.text }}</span>
          </article>
        </div>
      </div>

      <div class="visual-gallery-section">
        <div class="visual-gallery">
          <figure v-for="item in visualGallery" :key="item.title" class="visual-card">
            <img :src="item.image" :alt="item.title" loading="eager" />
            <span class="ai-node node-a"></span>
            <span class="ai-node node-b"></span>
            <span class="ai-node node-c"></span>
            <figcaption>
              <span>{{ item.label }}</span>
              <strong>{{ item.title }}</strong>
            </figcaption>
          </figure>
        </div>
      </div>

      <div class="products-section">
        <div class="section-heading">
          <p class="section-eyebrow">Products</p>
          <h2>
            VitaeAI数字生态全景
          </h2>
          <p>
            以智能为桥梁，VitaeAI智能体数字员工为企业构筑可持续在线的增长能力；
          </p>
          <p>
            以交互为入口，点指之间APP为用户开启数字价值的新连接。双产品引擎，贯通企业增长与用户生态，共同驱动可销售、可运营、可持续的数字商业未来。
          </p>
        </div>

        <div class="product-stack">
          <div v-if="productConfigLoading && !productConfigLoaded" class="product-config-state">
            正在读取官方商品配置...
          </div>
          <div v-else-if="productConfigError && !productConfigLoaded" class="product-config-state error">
            <p>{{ productConfigError }}</p>
            <button class="secondary-cta" type="button" @click="loadProductConfig">重新加载</button>
          </div>
          <template v-else>
            <article
              v-for="product in websiteProducts"
              :id="`product-${product.key}`"
              :key="product.key"
              :class="['product-showcase', { 'product-showcase-reverse': product.key === 'app' }]"
            >
              <div
                class="product-image"
                :style="{ backgroundImage: `linear-gradient(135deg, rgba(3, 9, 20, 0.02), rgba(3, 9, 20, 0.36)), url(${product.image})` }"
                aria-hidden="true"
              >
                <span class="ai-node node-a"></span>
                <span class="ai-node node-b"></span>
                <span class="ai-node node-c"></span>
                <span>{{ product.eyebrow }}</span>
              </div>
              <div class="product-copy">
                <p class="product-eyebrow">
                  {{ product.eyebrow }}
                </p>
                <h3>
                  {{ product.title }}
                </h3>
                <p class="product-subtitle">{{ product.subtitle }}</p>
                <p class="product-desc">{{ product.desc }}</p>
                <div class="product-tags">
                  <span v-for="point in product.points" :key="point" class="feature-tag">{{ point }}</span>
                </div>
                <div v-if="product.workflow" class="employee-workflow">
                  <div v-for="step in product.workflow" :key="step.title" class="workflow-step">
                    <span>{{ step.label }}</span>
                    <strong>{{ step.title }}</strong>
                    <p>{{ step.text }}</p>
                  </div>
                </div>
                <div class="metric-grid">
                  <div v-for="metric in product.metrics" :key="metric.label" class="metric-cell">
                    <strong>{{ metric.value }}</strong>
                    <span>{{ metric.label }}</span>
                  </div>
                </div>
              <div v-if="product.key === 'employee'" class="product-buy-panel">
                <div>
                  <span>{{ product.delivery }}</span>
                  <strong>{{ shouldHidePrice ? '价格以确认订单为准' : displayMoney(productConfig.unitPrice) }}</strong>
                </div>
                <button class="primary-cta order-cta" type="button" :disabled="!canCreateOrder" @click="startOrder">
                  {{ canCreateOrder ? '立即下单' : '暂未开放下单' }}
                </button>
              </div>
              <div v-else class="product-download-panel">
                <div>
                  <span>官方发布渠道</span>
                  <strong>扫码 / 复制链接下载</strong>
                </div>
                <div>
                  <button class="secondary-cta" type="button" @click="goToPath('/download')">APP 下载</button>
                  <button class="primary-cta" type="button" @click="goToPath('/register')">注册账号</button>
                </div>
              </div>
            </div>
          </article>
          </template>
        </div>
      </div>
    </section>

    <section v-else-if="activeView === 'register'" class="pc-feature-section">
      <PcRegisterPanel @navigate="goToPath" @registered="goToPath('/download')" />
    </section>

    <section v-else-if="activeView === 'download'" class="pc-feature-section">
      <PcDownloadPanel />
    </section>

    <section v-else-if="activeView === 'login-gate'" class="relative min-h-[calc(100vh-80px)]">
      <div class="trade-shell">
        <div class="trade-panel login-gate-panel">
          <p class="section-eyebrow">Login Required</p>
          <h1>请先登录</h1>
          <p>使用点指之间的账号登录。</p>
          <div class="form-grid login-form-grid">
            <label>
              <span>手机号</span>
              <input v-model.trim="loginForm.phone" type="tel" inputmode="numeric" autocomplete="tel" maxlength="11" />
            </label>
            <label>
              <span>密码</span>
              <input v-model="loginForm.password" type="password" autocomplete="current-password" />
            </label>
          </div>
          <p v-if="loginMessage" class="trade-message">{{ loginMessage }}</p>
          <div class="step-actions">
            <button class="primary-cta" type="button" :disabled="loginLoading" @click="submitLogin">
              {{ loginLoading ? '正在登录' : '登录' }}
            </button>
            <button class="secondary-cta" type="button" @click="goToPath('/')">
              返回首页
            </button>
          </div>
        </div>
      </div>
    </section>

    <section v-else-if="isTradeView" class="relative min-h-[calc(100vh-80px)]">
      <div class="trade-shell" :class="{ 'has-mobile-checkout-bar': mobileCheckoutBarVisible }">
        <a
          href="/"
          class="trade-back"
          @click.prevent="goToPath('/')"
        >
          <span>←</span> 返回首页
        </a>

        <div class="trade-heading">
          <p class="section-eyebrow">{{ currentPage.kicker }}</p>
          <h1>{{ currentPage.title }}</h1>
          <p>{{ currentPage.desc }}</p>
        </div>

        <div v-if="activeView === 'order-form'" class="checkout-grid">
          <section class="trade-panel checkout-form-panel">
            <div class="trade-panel-title">
              <span>01</span>
              <div>
                <h2>{{ isRevisingOrder ? '修改订单信息' : '填写订单表单' }}</h2>
                <p>{{ isRevisingOrder ? '修改实体收货信息和软件服务联系人，提交后重新进入后台审核。' : '填写实体收货信息和软件服务联系人，下一步再确认订购协议。' }}</p>
              </div>
            </div>

            <div class="mobile-order-summary" aria-label="商品与金额摘要">
              <div>
                <span>步骤 1 / 3 · {{ productConfig.productCode }}</span>
                <strong>{{ productConfig.productName }}</strong>
              </div>
              <div class="mobile-order-summary-price">
                <small>x {{ orderQuantity }}</small>
                <strong>{{ orderTotalLabel }}</strong>
              </div>
            </div>

            <div class="checkout-progress desktop-checkout-progress" aria-label="下单进度">
              <span class="active">{{ isRevisingOrder ? '修改订单信息' : '填写订单表单' }}</span>
              <span>{{ isRevisingOrder ? '重新提交审核' : '确认协议' }}</span>
              <span>{{ isRevisingOrder ? '等待后台审核' : '确认订单与支付' }}</span>
            </div>

            <div class="form-grid">
              <label v-if="addresses.length" class="form-wide">
                <span>选择已有收货地址</span>
                <select v-model="selectedAddressId" @change="applySelectedAddress">
                  <option value="">本次填写新地址</option>
                  <option v-for="address in addresses" :key="address.id" :value="address.id">
                    {{ address.receiverName }} · {{ address.receiverPhone }} · {{ fullAddress(address) }}
                  </option>
                </select>
              </label>
              <label :class="{ 'has-error': orderErrors.receiverName }">
                <span>收货人</span>
                <input
                  v-model="orderForm.receiverName"
                  data-order-field="receiverName"
                  autocomplete="shipping name"
                  :aria-invalid="Boolean(orderErrors.receiverName)"
                  :aria-describedby="orderErrors.receiverName ? 'receiver-name-error' : undefined"
                  @input="clearOrderError('receiverName')"
                />
                <small v-if="orderErrors.receiverName" id="receiver-name-error" class="form-error">{{ orderErrors.receiverName }}</small>
              </label>
              <label :class="{ 'has-error': orderErrors.receiverPhone }">
                <span>联系电话</span>
                <input
                  v-model="orderForm.receiverPhone"
                  data-order-field="receiverPhone"
                  type="tel"
                  inputmode="tel"
                  autocomplete="shipping tel"
                  :aria-invalid="Boolean(orderErrors.receiverPhone)"
                  :aria-describedby="orderErrors.receiverPhone ? 'receiver-phone-error' : undefined"
                  @input="clearOrderError('receiverPhone')"
                />
                <small v-if="orderErrors.receiverPhone" id="receiver-phone-error" class="form-error">{{ orderErrors.receiverPhone }}</small>
              </label>
              <label :class="{ 'has-error': orderErrors.province }">
                <span>省份</span>
                <select
                  v-model="orderForm.province"
                  data-order-field="province"
                  autocomplete="shipping address-level1"
                  :aria-invalid="Boolean(orderErrors.province)"
                  @change="handleProvinceChange"
                >
                  <option value="">请选择省份</option>
                  <option v-for="province in provinceOptions" :key="province.value" :value="province.value">
                    {{ province.label }}
                  </option>
                </select>
                <small v-if="orderErrors.province" class="form-error">{{ orderErrors.province }}</small>
              </label>
              <label :class="{ 'has-error': orderErrors.city }">
                <span>城市</span>
                <select
                  v-model="orderForm.city"
                  data-order-field="city"
                  autocomplete="shipping address-level2"
                  :aria-invalid="Boolean(orderErrors.city)"
                  :disabled="cityOptions.length === 0"
                  @change="handleCityChange"
                >
                  <option value="">请选择城市</option>
                  <option v-for="city in cityOptions" :key="city.value" :value="city.value">
                    {{ city.label }}
                  </option>
                </select>
                <small v-if="orderErrors.city" class="form-error">{{ orderErrors.city }}</small>
              </label>
              <label :class="{ 'has-error': orderErrors.district }">
                <span>区县</span>
                <select
                  v-model="orderForm.district"
                  data-order-field="district"
                  autocomplete="shipping address-level3"
                  :aria-invalid="Boolean(orderErrors.district)"
                  :disabled="districtOptions.length === 0"
                  @change="handleDistrictChange"
                >
                  <option value="">请选择区县</option>
                  <option v-for="district in districtOptions" :key="district.value" :value="district.value">
                    {{ district.label }}
                  </option>
                </select>
                <small v-if="orderErrors.district" class="form-error">{{ orderErrors.district }}</small>
              </label>
              <label>
                <span>购买数量</span>
                <span class="quantity-stepper">
                  <button type="button" aria-label="减少购买数量" :disabled="isRevisingOrder || orderQuantity <= 1" @click="changeQuantity(-1)">-</button>
                  <input
                    v-model.number="orderForm.quantity"
                    :disabled="isRevisingOrder"
                    min="1"
                    step="1"
                    type="number"
                    inputmode="numeric"
                    aria-label="购买数量"
                    @blur="normalizeQuantity"
                  />
                  <button type="button" aria-label="增加购买数量" :disabled="isRevisingOrder" @click="changeQuantity(1)">+</button>
                </span>
              </label>
              <label class="form-wide" :class="{ 'has-error': orderErrors.addressDetail }">
                <span>详细地址</span>
                <input
                  v-model="orderForm.addressDetail"
                  data-order-field="addressDetail"
                  autocomplete="shipping street-address"
                  :aria-invalid="Boolean(orderErrors.addressDetail)"
                  @input="clearOrderError('addressDetail')"
                />
                <small v-if="orderErrors.addressDetail" class="form-error">{{ orderErrors.addressDetail }}</small>
              </label>
              <label class="form-wide same-contact-toggle">
                <input v-model="sameServiceContact" type="checkbox" />
                <span>
                  <strong>软件服务联系人同收货人</strong>
                  <small>联系人与电话将随收货信息自动同步</small>
                </span>
              </label>
              <label v-if="!sameServiceContact" :class="{ 'has-error': orderErrors.serviceContact }">
                <span>软件服务联系人</span>
                <input
                  v-model="orderForm.serviceContact"
                  data-order-field="serviceContact"
                  autocomplete="name"
                  :aria-invalid="Boolean(orderErrors.serviceContact)"
                  @input="clearOrderError('serviceContact')"
                />
                <small v-if="orderErrors.serviceContact" class="form-error">{{ orderErrors.serviceContact }}</small>
              </label>
              <label v-if="!sameServiceContact" :class="{ 'has-error': orderErrors.servicePhone }">
                <span>服务联系电话</span>
                <input
                  v-model="orderForm.servicePhone"
                  data-order-field="servicePhone"
                  type="tel"
                  inputmode="tel"
                  autocomplete="tel"
                  :aria-invalid="Boolean(orderErrors.servicePhone)"
                  @input="clearOrderError('servicePhone')"
                />
                <small v-if="orderErrors.servicePhone" class="form-error">{{ orderErrors.servicePhone }}</small>
              </label>
              <label class="form-wide">
                <span>订单备注</span>
                <textarea v-model="orderForm.remark" autocomplete="off" placeholder="选填，如有交付时间等特殊要求可在此说明"></textarea>
              </label>
            </div>
            <p v-if="orderClosedMessage" class="trade-message">{{ orderClosedMessage }}</p>
            <p v-if="tradeMessage" class="trade-message">{{ tradeMessage }}</p>
            <button class="primary-cta trade-main-action desktop-trade-action" type="button" :disabled="tradeLoading || (!isRevisingOrder && !canCreateOrder)" @click="submitOrderForm">
              {{ isRevisingOrder ? '重新提交审核' : '下一步，确认协议' }}
            </button>
          </section>

          <aside class="trade-panel order-summary-panel">
            <div class="summary-product">
              <span class="summary-sku">{{ productConfig.productCode }}</span>
              <h2>{{ productConfig.productName }}</h2>
              <p>{{ productConfig.productType }} · {{ productConfig.servicePeriod }}</p>
            </div>
            <div class="summary-includes">
              <span v-for="item in productIncludes" :key="item">{{ item }}</span>
              <span v-if="productConfig.giftComputePower">{{ productConfig.giftComputePower }}</span>
            </div>
            <div class="summary-row">
              <span>单价</span>
              <strong>{{ displayMoney(productConfig.unitPrice) }}</strong>
            </div>
            <div class="summary-row">
              <span>数量</span>
              <strong>x {{ orderQuantity }}</strong>
            </div>
            <div class="summary-total">
              <span>应付金额</span>
              <strong>{{ orderTotalLabel }}</strong>
            </div>
            <button class="secondary-cta trade-main-action" type="button" @click="goToPath('/user/address')">
              管理收货地址
            </button>
          </aside>
        </div>

        <div v-if="activeView === 'order-agreement'" class="agreement-layout">
          <section class="trade-panel agreement-panel">
            <div class="trade-panel-title">
              <span>02</span>
              <div>
                <h2>确认订购协议</h2>
                <p>请逐份阅读并确认协议。创建订单时将保存协议版本与签署快照。</p>
              </div>
            </div>
            <div class="checkout-progress" aria-label="下单进度">
              <span>填写订单表单</span>
              <span class="active">确认协议</span>
              <span>确认订单与支付</span>
            </div>
            <div v-if="orderAgreements.length" class="agreement-switcher" aria-label="协议切换">
              <button
                v-for="(agreement, index) in orderAgreements"
                :key="agreement.key"
                type="button"
                :class="{ active: activeAgreement?.key === agreement.key }"
                @click="selectAgreement(index)"
              >
                <span>{{ agreement.label }}</span>
                <strong>{{ agreement.title }}</strong>
              </button>
            </div>
            <div v-if="activeAgreement" class="agreement-viewer">
              <article class="agreement-document">
                <div class="agreement-document-head">
                  <span>{{ activeAgreement.label }}</span>
                  <h3>《{{ activeAgreement.title }}》</h3>
                </div>
                <div class="agreement-richtext" v-html="activeAgreement.html"></div>
              </article>
            </div>
            <p v-else class="empty-state">暂无可确认的订购协议，请联系管理员配置已发布协议。</p>
            <div class="agreement-checklist">
              <label v-for="agreement in orderAgreements" :key="agreement.key" class="agreement-row">
                <input v-model="acceptedAgreementKeys" type="checkbox" :value="agreement.key" />
                <span>我已阅读并同意《{{ agreement.title }}》</span>
              </label>
            </div>
            <div class="step-actions agreement-step-actions">
              <button class="secondary-cta" type="button" @click="goToPath('/order/form')">
                上一步
              </button>
              <button class="primary-cta desktop-trade-action" type="button" :disabled="tradeLoading || !canCreateOrder || !allAgreementsAccepted" @click="submitAgreement">
                {{ tradeLoading ? '正在创建订单' : agreementButtonText }}
              </button>
            </div>
          </section>

          <aside class="trade-panel status-rail">
            <div v-for="step in orderSteps" :key="step.label" class="status-step">
              <span>{{ step.label }}</span>
              <div>
                <strong>{{ step.title }}</strong>
                <p>{{ step.text }}</p>
              </div>
            </div>
          </aside>
        </div>

        <div v-if="activeView === 'order-pay'" class="payment-layout">
          <section class="trade-panel payment-card">
            <div class="payment-copy">
              <p class="section-eyebrow">Confirm & Pay</p>
              <h2>{{ paymentTitle }}</h2>
              <p>{{ paymentHint }}</p>
              <div class="payment-amount">{{ currentOrder ? displayMoney(currentOrder.payAmount || currentOrder.totalAmount) : orderTotalLabel }}</div>
              <div v-if="canContinuePay(currentOrder)" class="payment-deadline" :class="{ expired: paymentExpired }">
                <span>支付截止</span>
                <strong>{{ paymentCountdownText }}</strong>
              </div>
              <div v-if="currentOrder" class="payment-order-meta">
                <span>{{ currentOrder.productName }}</span>
                <span>订单 {{ currentOrder.orderNo }}</span>
                <span>{{ currentOrder.receiverName }} · {{ currentOrder.receiverPhone }}</span>
              </div>
              <div v-if="canContinuePay(currentOrder)" class="payment-method-field">
                <strong class="payment-method-label">选择支付方式</strong>
                <div class="payment-method-switch" aria-label="选择支付方式">
                  <button
                    type="button"
                    :class="{ active: selectedPaymentMethod === 'ALIPAY' }"
                    :disabled="paymentWasOpened"
                    @click="selectPaymentMethod('ALIPAY')"
                  >
                    支付宝
                  </button>
                  <button
                    type="button"
                    :class="{ active: selectedPaymentMethod === 'BANK_TRANSFER' }"
                    :disabled="paymentWasOpened || !bankTransferAvailable"
                    :aria-describedby="!bankTransferAvailable ? 'bank-transfer-unavailable' : undefined"
                    @click="selectPaymentMethod('BANK_TRANSFER')"
                  >
                    银行卡转账
                  </button>
                </div>
                <small v-if="!bankTransferAvailable" id="bank-transfer-unavailable" class="payment-method-unavailable">
                  银行卡转账暂不可用，请使用支付宝支付
                </small>
              </div>
              <div v-if="showBankAccountDetails" class="bank-transfer-panel">
                <div class="bank-transfer-heading">
                  <div>
                    <span>银行卡转账</span>
                    <strong>{{ bankTransferPending ? '转账信息等待后台核对' : '按步骤完成转账并提交核对' }}</strong>
                  </div>
                  <em v-if="!bankTransferPending">请勿向其他账户转账</em>
                </div>
                <dl>
                  <div>
                    <dt>开户银行</dt>
                    <dd>{{ bankAccountDetails.bankName || '-' }}</dd>
                  </div>
                  <div>
                    <dt>账户名称</dt>
                    <dd>{{ bankAccountDetails.bankAccountName || '-' }}</dd>
                  </div>
                  <div>
                    <dt>银行账号</dt>
                    <dd class="bank-account-number">{{ bankAccountDetails.bankAccountNumber || '-' }}</dd>
                    <button type="button" @click="copyBankAccount(bankAccountDetails.bankAccountNumber)">复制</button>
                  </div>
                  <div v-if="bankAccountDetails.bankBranchName">
                    <dt>开户支行</dt>
                    <dd>{{ bankAccountDetails.bankBranchName }}</dd>
                  </div>
                </dl>
                <p v-if="bankAccountDetails.bankTransferInstructions">{{ bankAccountDetails.bankTransferInstructions }}</p>
                <div v-if="canContinuePay(currentOrder)" class="bank-transfer-guide">
                  <div class="bank-transfer-guide-head">
                    <span>转账指引</span>
                    <strong>请依次完成以下 4 步</strong>
                  </div>
                  <ol>
                    <li>
                      <span>1</span>
                      <div>
                        <strong>复制收款账号</strong>
                        <p>打开手机银行或网上银行，选择银行卡转账。</p>
                      </div>
                    </li>
                    <li>
                      <span>2</span>
                      <div>
                        <strong>转账订单金额</strong>
                        <p>本次应付 <b>{{ displayMoney(currentOrder.payAmount || currentOrder.totalAmount) }}</b>，请勿拆分转账。</p>
                      </div>
                    </li>
                    <li>
                      <span>3</span>
                      <div>
                        <strong>附言填写订单号</strong>
                        <p class="bank-transfer-order-no">{{ currentOrder.orderNo }}</p>
                        <button type="button" @click="copyOrderNo(currentOrder.orderNo)">复制订单号</button>
                      </div>
                    </li>
                    <li>
                      <span>4</span>
                      <div>
                        <strong>返回提交核对</strong>
                        <p>确认银行已受理转账后，点击“我已完成转账，提交核对”。</p>
                      </div>
                    </li>
                  </ol>
                </div>
                <div v-else-if="bankTransferPending" class="bank-transfer-pending-note">
                  <strong>已提交转账核对</strong>
                  <p>后台人员将根据订单号和到账记录进行核对，确认到账后订单会自动进入审核，无需重复转账或提交。</p>
                </div>
                <p v-else class="bank-transfer-notice">该笔银行卡转账已经处理，请以当前支付和审核状态为准。</p>
              </div>
              <div v-if="currentOrder" class="payment-status-board">
                <div>
                  <span>订单状态</span>
                  <strong>{{ orderDisplayStatusText(currentOrder) }}</strong>
                </div>
                <div>
                  <span>支付状态</span>
                  <strong>{{ orderPayStatusText(currentOrder) }}</strong>
                </div>
                <div>
                  <span>退款状态</span>
                  <strong>{{ refundStatusText(currentOrder.refundStatus) }}</strong>
                </div>
                <div>
                  <span>审核状态</span>
                  <strong>{{ orderAuditText(currentOrder.orderAuditStatus) }}</strong>
                </div>
                <div v-if="toNumber(currentOrder.bankTransferStatus) > 0">
                  <span>转账核对</span>
                  <strong>{{ bankTransferStatusText(currentOrder.bankTransferStatus) }}</strong>
                </div>
              </div>
              <p v-if="canReviseOrder(currentOrder) && currentOrder.orderAuditRemark" class="trade-message">
                审核说明：{{ currentOrder.orderAuditRemark }}
              </p>
              <p v-if="tradeMessage" class="trade-message">{{ tradeMessage }}</p>
              <div class="payment-actions desktop-payment-actions" :class="{ 'has-cancel-action': canCancelOrder(currentOrder) }">
                <button
                  v-if="canContinuePay(currentOrder) && selectedPaymentMethod === 'BANK_TRANSFER'"
                  class="primary-cta trade-main-action"
                  type="button"
                  :disabled="tradeLoading || paymentExpired"
                  @click="openBankTransferConfirm"
                >
                  {{ toNumber(currentOrder?.bankTransferStatus) === 3 ? '重新提交转账核对' : '我已完成转账，提交核对' }}
                </button>
                <button
                  v-else-if="canContinuePay(currentOrder) && !paymentWasOpened"
                  class="primary-cta trade-main-action"
                  type="button"
                  :disabled="tradeLoading || paymentExpired"
                  @click="submitPayment"
                >
                  {{ tradeLoading ? '正在打开支付宝' : paymentButtonText }}
                </button>
                <button
                  v-else-if="canContinuePay(currentOrder)"
                  class="primary-cta trade-main-action"
                  type="button"
                  :disabled="tradeLoading || paymentCheckLoading"
                  @click="checkPaymentStatus(true)"
                >
                  {{ paymentCheckLoading ? '正在查询支付结果' : '我已完成支付，重新检查' }}
                </button>
                <button
                  v-else-if="canReviseOrder(currentOrder)"
                  class="primary-cta trade-main-action"
                  type="button"
                  @click="startOrderRevision(currentOrder)"
                >
                  修改订单信息
                </button>
                <button
                  v-else-if="currentOrder && !paymentSettlementPending"
                  class="secondary-cta trade-main-action"
                  type="button"
                  @click="goToPath(`/user/orders/${currentOrder.id}`)"
                >
                  查看订单详情
                </button>
                <button v-else class="secondary-cta trade-main-action" type="button" disabled>
                  {{ paymentSettlementPending ? (bankTransferPending ? '转账待核对' : '等待支付结果') : '正在读取订单' }}
                </button>
                <button
                  v-if="canCancelOrder(currentOrder)"
                  class="secondary-cta danger-secondary-cta trade-main-action"
                  type="button"
                  :disabled="tradeLoading || paymentCheckLoading"
                  @click="openCancelConfirm"
                >
                  取消订单
                </button>
              </div>
              <details v-if="canContinuePay(currentOrder) && selectedPaymentMethod === 'ALIPAY'" class="payment-help">
                <summary>支付遇到问题</summary>
                <p>请先重新检查支付结果。仅在确认支付宝未扣款时，再次打开支付页面。</p>
                <div>
                  <button type="button" :disabled="tradeLoading || paymentCheckLoading" @click="checkPaymentStatus(true)">重新检查</button>
                  <button type="button" :disabled="tradeLoading || paymentExpired" @click="submitPayment">重新打开支付宝</button>
                  <a :href="`mailto:${contactEmail}`">联系客服</a>
                </div>
              </details>
            </div>
          </section>

          <aside class="trade-panel status-rail">
            <div v-for="step in orderSteps" :key="step.label" class="status-step">
              <span>{{ step.label }}</span>
              <div>
                <strong>{{ step.title }}</strong>
                <p>{{ step.text }}</p>
              </div>
            </div>
          </aside>
        </div>

        <div v-if="activeView === 'orders'" class="trade-panel orders-panel">
          <div class="orders-toolbar">
            <div>
              <p class="section-eyebrow">My Orders</p>
              <h2>我的订单</h2>
            </div>
            <button class="secondary-cta" type="button" @click="startOrder">
              再次购买
            </button>
          </div>
          <p v-if="orders.length === 0" class="empty-state">暂无订单，点击“再次购买”创建第一笔官网订单。</p>
          <button
            v-for="order in orders"
            :key="order.id"
            class="order-row"
            type="button"
            @click="openOrderDetail(order)"
          >
            <span>
              <small>订单编号</small>
              <strong>{{ order.orderNo }}</strong>
            </span>
            <span>
              <small>产品</small>
              <strong>{{ order.productName }}</strong>
            </span>
            <span>
              <small>状态</small>
              <strong>{{ orderStatusText(order.orderStatus) }}</strong>
            </span>
            <span>
              <small>金额</small>
              <strong>{{ displayMoney(order.totalAmount) }}</strong>
            </span>
            <span v-if="receiptCountdownVisible(order)" class="order-receipt-countdown">
              <small>{{ receiptCountdownLabel(order) }}</small>
              <strong>{{ receiptCountdownTextForOrder(order) }}</strong>
            </span>
            <em>查看详情</em>
          </button>
        </div>

        <div v-if="activeView === 'address'" class="trade-panel address-panel">
          <div class="orders-toolbar">
            <div>
              <p class="section-eyebrow">Addresses</p>
              <h2>收货地址</h2>
            </div>
            <button class="secondary-cta" type="button" @click="goToPath('/user')">
              返回用户中心
            </button>
          </div>
          <div class="address-list">
            <p v-if="addresses.length === 0" class="empty-state">暂无收货地址，下单后会自动保存。</p>
            <article v-for="address in addresses" :key="address.id" class="address-card">
              <span>{{ address.defaultFlag === 1 ? '默认地址' : '收货地址' }}</span>
              <strong>{{ address.receiverName }} · {{ address.receiverPhone }}</strong>
              <p>{{ fullAddress(address) }}</p>
            </article>
          </div>
        </div>

        <div v-if="activeView === 'order-detail' && currentOrder" class="order-detail-grid">
          <section class="trade-panel detail-main">
            <div class="detail-hero">
              <span>{{ orderStatusText(currentOrder.orderStatus) }}</span>
              <h2>{{ currentOrder.productName }}</h2>
              <p>订单 {{ currentOrder.orderNo }} · 创建于 {{ formatTime(currentOrder.createTime) }}</p>
            </div>
            <div class="detail-facts">
              <div>
                <span>支付状态</span>
                <strong>{{ orderPayStatusText(currentOrder) }}</strong>
              </div>
              <div>
                <span>退款状态</span>
                <strong>{{ refundStatusText(currentOrder.refundStatus) }}</strong>
              </div>
              <div>
                <span>审核状态</span>
                <strong>{{ orderAuditText(currentOrder.orderAuditStatus) }}</strong>
              </div>
              <div>
                <span>交付状态</span>
                <strong>{{ deliveryStatusText(currentOrder.deliveryStatus) }}</strong>
              </div>
            </div>
            <div v-if="receiptCountdownVisible(currentOrder)" class="pc-receipt-countdown" aria-live="polite">
              <div>
                <span>{{ receiptCountdownLabel(currentOrder) }}</span>
                <strong>{{ receiptCountdownTextForOrder(currentOrder) }}</strong>
              </div>
              <button
                v-if="canExtendReceipt(currentOrder)"
                class="secondary-cta"
                type="button"
                :disabled="tradeLoading"
                @click="extendReceipt"
              >
                延长收货 48 小时
              </button>
            </div>
            <div class="detail-section">
              <h3>收货与服务信息</h3>
              <p>{{ currentOrder.receiverName }} · {{ currentOrder.receiverPhone }}</p>
              <p>{{ fullAddress(currentOrder) }}</p>
              <p>软件服务联系人：{{ currentOrder.serviceContact }} · {{ currentOrder.servicePhone }}</p>
              <p v-if="canReviseOrder(currentOrder) && currentOrder.orderAuditRemark">审核说明：{{ currentOrder.orderAuditRemark }}</p>
            </div>
            <p v-if="tradeMessage" class="trade-message">{{ tradeMessage }}</p>
            <div class="detail-actions">
              <button
                v-if="canReviseOrder(currentOrder)"
                class="primary-cta trade-main-action"
                type="button"
                @click="startOrderRevision(currentOrder)"
              >
                修改订单信息
              </button>
              <button
                v-else-if="canContinuePay(currentOrder)"
                class="primary-cta trade-main-action"
                type="button"
                :disabled="tradeLoading"
                @click="continueOrderPayment(currentOrder)"
              >
                {{ tradeLoading ? '正在处理' : '继续支付' }}
              </button>
              <button
                v-else
                class="primary-cta trade-main-action"
                type="button"
                :disabled="tradeLoading || !canConfirmReceipt(currentOrder)"
                @click="openReceiptConfirm"
              >
                {{ tradeLoading ? '正在提交' : receiptActionText(currentOrder) }}
              </button>
              <button
                v-if="canCancelOrder(currentOrder)"
                class="secondary-cta trade-main-action"
                type="button"
                :disabled="tradeLoading"
                @click="openCancelConfirm"
              >
                取消订单
              </button>
            </div>
          </section>

          <aside class="trade-panel timeline-panel">
            <h2>订单进度</h2>
            <div v-for="item in orderTimeline(currentOrder)" :key="item.title" class="timeline-item">
              <span></span>
              <div>
                <strong>{{ item.title }}</strong>
                <p>{{ item.text }}</p>
                <small>{{ item.time }}</small>
              </div>
            </div>
          </aside>
        </div>

        <div v-if="receiptConfirmVisible" class="confirm-mask" role="dialog" aria-modal="true">
          <div class="confirm-dialog">
            <span>确认收货</span>
            <h2>请确认已经收到实体产品并完成基础验收</h2>
            <p>提交后订单将进入 48 小时人工审核，审核通过后完成确认收货流程。</p>
            <div class="confirm-actions">
              <button class="secondary-cta" type="button" @click="receiptConfirmVisible = false">取消</button>
              <button class="primary-cta" type="button" :disabled="tradeLoading" @click="submitReceipt">
                {{ tradeLoading ? '正在提交' : '确认提交' }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="cancelConfirmVisible" class="confirm-mask" role="dialog" aria-modal="true">
          <div class="confirm-dialog">
            <span>取消订单</span>
            <h2>{{ cancelDialogTitle }}</h2>
            <p>{{ cancelDialogText }}</p>
            <p v-if="cancelMessage" class="trade-message">{{ cancelMessage }}</p>
            <div class="confirm-actions">
              <button class="secondary-cta" type="button" @click="cancelConfirmVisible = false">再想想</button>
              <button class="primary-cta" type="button" :disabled="tradeLoading" @click="submitCancelOrder">
                {{ tradeLoading ? '正在取消' : '确认取消' }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="bankTransferConfirmVisible" class="confirm-mask" role="dialog" aria-modal="true">
          <div class="confirm-dialog">
            <span>提交转账核对</span>
            <h2>确认已经完成银行卡转账？</h2>
            <p>请确认收款账号无误，并已在转账附言中填写订单号 {{ currentOrder?.orderNo }}。提交后将等待后台核对到账。</p>
            <div class="confirm-actions">
              <button class="secondary-cta" type="button" @click="bankTransferConfirmVisible = false">返回检查</button>
              <button class="primary-cta" type="button" :disabled="tradeLoading" @click="submitBankTransfer">
                {{ tradeLoading ? '正在提交' : '确认提交核对' }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="activeView === 'user-center'" class="user-center-grid">
          <section class="trade-panel user-profile-panel">
            <div class="user-profile-head">
              <div class="user-profile-identity">
                <div class="user-avatar">张</div>
                <div>
                  <span>线上用户</span>
                  <h2>{{ userProfile.name }}</h2>
                  <p>{{ userProfile.phone }}</p>
                </div>
              </div>
              <button class="logout-button" type="button" @click="logout">
                退出登录
              </button>
            </div>
            <div class="user-stats">
              <div>
                <span>官网订单</span>
                <strong>{{ orders.length }}</strong>
              </div>
              <div>
                <span>待支付</span>
                <strong>{{ pendingPayCount }}</strong>
              </div>
              <div>
                <span>已完成</span>
                <strong>{{ finishedCount }}</strong>
              </div>
            </div>
            <div class="user-info-list">
              <div>
                <span>默认收货地址</span>
                <strong>{{ defaultAddressText }}</strong>
              </div>
              <div>
                <span>软件服务联系人</span>
                <strong>{{ latestServiceContact }}</strong>
              </div>
              <div>
                <span>账户状态</span>
                <strong>正常</strong>
              </div>
            </div>
          </section>

          <aside class="trade-panel user-action-panel">
            <div class="contact-card">
              <button class="contact-qr-button" type="button" @click="openQrPreview">
                <img
                  v-if="productConfig.customerServiceQrCode"
                  class="wechat-qr-image"
                  :src="productConfig.customerServiceQrCode"
                  alt="客服微信二维码"
                />
                <span v-else class="wechat-qr" aria-label="客服微信二维码">
                  <span v-for="cell in wechatQrCells" :key="cell" :style="{ gridArea: cell }"></span>
                </span>
                <span class="contact-qr-hint">点击预览</span>
              </button>
              <div class="contact-content">
                <span>联系方式</span>
                <strong>客服微信</strong>
                <p>扫码添加客服，咨询交付、审核与服务开通。</p>
                <button class="contact-email-button" type="button" @click="copyContactEmail">
                  <small>联系邮箱</small>
                  <b>{{ contactEmail }}</b>
                  <em>{{ emailCopyText }}</em>
                </button>
              </div>
            </div>
            <button class="user-action-card" type="button" @click="goToPath('/user/orders')">
              <span>订单</span>
              <strong>我的订单</strong>
              <p>查看支付状态、审核进度和交付信息。</p>
            </button>
            <button class="user-action-card" type="button" @click="goToPath('/user/address')">
              <span>地址</span>
              <strong>收货地址</strong>
              <p>维护实体产品配送地址。</p>
            </button>
          </aside>
        </div>

        <div
          v-if="mobileCheckoutBarVisible"
          class="mobile-checkout-bar"
          :class="{ 'is-keyboard-open': mobileKeyboardOpen }"
          aria-label="当前结算操作"
        >
          <div class="mobile-checkout-total">
            <span>{{ mobileCheckoutMeta }}</span>
            <strong>{{ mobileCheckoutAmount }}</strong>
          </div>
          <div
            class="mobile-checkout-actions"
            :class="{ 'has-cancel-action': activeView === 'order-pay' && canCancelOrder(currentOrder) }"
          >
            <button
              v-if="activeView === 'order-pay' && canCancelOrder(currentOrder)"
              class="secondary-cta danger-secondary-cta"
              type="button"
              :disabled="tradeLoading || paymentCheckLoading"
              @click="openCancelConfirm"
            >
              取消订单
            </button>
            <button
              v-if="activeView === 'order-form'"
              class="primary-cta"
              type="button"
              :disabled="tradeLoading || (!isRevisingOrder && !canCreateOrder)"
              @click="submitOrderForm"
            >
              {{ isRevisingOrder ? '重新提交' : '确认信息' }}
            </button>
            <button
              v-else-if="activeView === 'order-agreement'"
              class="primary-cta"
              type="button"
              :disabled="tradeLoading || !canCreateOrder || !allAgreementsAccepted"
              @click="submitAgreement"
            >
              {{ tradeLoading ? '创建中' : '确认协议' }}
            </button>
            <button
              v-else-if="canContinuePay(currentOrder) && selectedPaymentMethod === 'BANK_TRANSFER'"
              class="primary-cta"
              type="button"
              :disabled="tradeLoading || paymentExpired"
              @click="openBankTransferConfirm"
            >
              提交转账核对
            </button>
            <button
              v-else-if="canContinuePay(currentOrder) && paymentWasOpened"
              class="primary-cta"
              type="button"
              :disabled="tradeLoading || paymentCheckLoading"
              @click="checkPaymentStatus(true)"
            >
              {{ paymentCheckLoading ? '查询中' : '检查支付结果' }}
            </button>
            <button
              v-else-if="canContinuePay(currentOrder)"
              class="primary-cta"
              type="button"
              :disabled="tradeLoading || paymentExpired"
              @click="submitPayment"
            >
              {{ selectedPaymentMethod === 'BANK_TRANSFER' ? '提交转账核对' : '打开支付宝' }}
            </button>
            <button
              v-else-if="!paymentSettlementPending"
              class="secondary-cta"
              type="button"
              @click="goToPath(currentOrder ? `/user/orders/${currentOrder.id}` : '/user/orders')"
            >
              查看订单
            </button>
          </div>
        </div>
      </div>
    </section>

    <section v-else class="relative min-h-[calc(100vh-80px)]">
      <div class="subpage-shell">
        <a
          href="/"
          class="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-text-secondary transition-colors hover:text-white"
          @click.prevent="goToPath('/')"
        >
          <span class="text-lg leading-none">←</span> 返回首页
        </a>

        <div class="subpage-heading">
          <p class="section-eyebrow justify-center">{{ currentPage.kicker }}</p>
          <h1>
            {{ currentPage.title }}
          </h1>
          <p>{{ currentPage.desc }}</p>
        </div>

        <div v-if="activeView === 'about'" class="company-panel">
          <div class="company-copy" style="flex: 1">
            <p class="section-eyebrow">Company</p>
            <h2 class="company-name">
              {{ companyProfile.title }}
            </h2>
            <p>{{ companyProfile.intro }}</p>
            <div class="company-tags">
              <span v-for="tag in companyProfile.tags" :key="tag" class="feature-tag">{{ tag }}</span>
            </div>
          </div>

          <figure class="subpage-visual company-visual" style="flex: 1">
            <img :src="companyProfile.visual.image" :alt="companyProfile.visual.title" loading="eager" />
            <span class="ai-hud-label">AI OPERATING MAP</span>
            <span class="ai-node node-a"></span>
            <span class="ai-node node-b"></span>
            <span class="ai-node node-c"></span>
            <figcaption>
              <strong>{{ companyProfile.visual.title }}</strong>
            </figcaption>
          </figure>
          <div class="company-facts">
            <div v-for="fact in companyProfile.facts" :key="fact.label" class="fact-row">
              <span>{{ fact.label }}</span>
              <strong>{{ fact.value }}</strong>
            </div>
          </div>
        </div>



        <div v-if="activeView === 'culture'" class="culture-panel">
          <figure class="subpage-visual culture-visual">
            <img :src="cultureContent.visual.image" :alt="cultureContent.visual.title" loading="eager" />
            <span class="ai-hud-label">AI · VALUE</span>
            <span class="ai-node node-a"></span>
            <span class="ai-node node-b"></span>
            <span class="ai-node node-c"></span>
            <figcaption>
              <strong>{{ cultureContent.visual.title }}</strong>
            </figcaption>
          </figure>
          <div class="culture-copy">
            <p class="product-eyebrow">
              CULTURAL COMPASS
            </p>
            <p class="culture-lead">{{ cultureContent.lead }}</p>
            <p class="product-eyebrow">
              OUR BELIEF
            </p>
            <p class="culture-mission">{{ cultureContent.mission }}</p>
          </div>
          <div class="culture-values">
            <span
              v-for="(item, index) in cultureContent.highlights"
              :key="item.label"
              class="culture-value"
              :data-index="String(index + 1).padStart(2, '0')"
            >
              <p class="product-eyebrow culture-value-label">
                {{ item.label }}
              </p>
              <p class="culture-value-title">
                {{ item.text }}
              </p>
              <p class="culture-value-desc">
                {{ item.desc }}
              </p>
            </span>
          </div>
          <blockquote class="culture-quote">
            <p class="product-eyebrow">
              OUR COMMITMENT
            </p>
            {{ cultureContent.closing }}
          </blockquote>
        </div>
      </div>
    </section>

    <div
      v-if="qrPreviewVisible"
      class="confirm-mask qr-preview-mask"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-preview-title"
      @click.self="qrPreviewVisible = false"
    >
      <div class="confirm-dialog qr-preview-dialog">
        <span>联系方式</span>
        <h2 id="qr-preview-title">客服微信二维码</h2>
        <button class="qr-preview-close" type="button" aria-label="关闭二维码预览" @click="qrPreviewVisible = false">
          X
        </button>
        <img
          v-if="productConfig.customerServiceQrCode"
          class="qr-preview-image"
          :src="productConfig.customerServiceQrCode"
          alt="客服微信二维码预览"
        />
        <div v-else class="wechat-qr qr-preview-fallback" aria-label="客服微信二维码占位">
          <span v-for="cell in wechatQrCells" :key="cell" :style="{ gridArea: cell }"></span>
        </div>
        <p>打开微信扫一扫，添加客服咨询交付、审核与服务开通。</p>
      </div>
    </div>

    <Transition name="fade">
      <div v-if="developingDialogVisible" class="confirm-mask" role="dialog" aria-modal="true" aria-labelledby="developing-dialog-title">
        <div class="confirm-dialog">
          <span>Coming Soon</span>
          <h2 id="developing-dialog-title">开发中</h2>
          <p>该功能正在打磨中，暂时还不能使用。</p>
          <div class="confirm-actions">
            <button class="primary-cta" type="button" @click="developingDialogVisible = false">知道了</button>
          </div>
        </div>
      </div>
    </Transition>

    <footer class="relative border-t border-white/[0.06]">
      <div class="mx-auto max-w-7xl px-6 py-10 lg:px-8">
        <div class="flex flex-col items-center gap-3 text-sm">
          <div class="flex items-center gap-2 text-text-secondary">
            <span class="text-text-muted">联系我们</span>
            <span class="text-white/20">·</span>
            <a :href="`mailto:${contactEmail}`" class="transition-colors hover:text-brand-light">{{ contactEmail }}</a>
          </div>
          <div class="flex flex-wrap items-center justify-center gap-2 text-text-muted">
            <span>© 2026 {{ company.brand }}</span>
            <span class="text-white/20">·</span>
            <span>{{ company.legalName }}</span>
          </div>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noreferrer"
            class="text-text-muted underline underline-offset-4 transition-colors hover:text-white"
          >
            {{ company.icp }}
          </a>
        </div>
      </div>
    </footer>
  </main>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import HeroCanvas from './components/HeroCanvas.vue'
import PcDownloadPanel from './components/pc/PcDownloadPanel.vue'
import PcRegisterPanel from './components/pc/PcRegisterPanel.vue'
import {
  companyProfile,
  cultureContent,
  homeSections,
  navItems,
  products,
  visualGallery
} from './data/site'
import {company} from "./data/company.js";
import { loginWithPassword } from './api/auth'
import {
  cancelWebsiteOrder,
  confirmWebsiteReceipt,
  createWebsiteAlipayPay,
  createWebsiteOrder,
  getWebsiteAddresses,
  getWebsiteOrder,
  getWebsiteOrders,
  getWebsiteProductConfig,
  reviseWebsiteOrder,
  submitWebsiteBankTransfer,
  extendWebsiteReceipt
} from './api/trade'

const activeView = ref('home')
const mobileMenuOpen = ref(false)
const tradeLoading = ref(false)
const tradeMessage = ref('')
const authToken = ref(localStorage.getItem('dzzj_website_token') || '')
const loginLoading = ref(false)
const loginMessage = ref('')
const loginReturnPath = ref('/order/form')
const loginForm = ref({
  phone: localStorage.getItem('dzzj_website_phone') || '',
  password: ''
})
const orders = ref([])
const addresses = ref([])
const currentOrder = ref(null)
const selectedAddressId = ref('')
const sameServiceContact = ref(true)
const orderErrors = ref({})
const mobileKeyboardOpen = ref(false)
const paymentCheckLoading = ref(false)
const paymentWasOpened = ref(false)
const developingDialogVisible = ref(false)
const receiptConfirmVisible = ref(false)
const cancelConfirmVisible = ref(false)
const bankTransferConfirmVisible = ref(false)
const selectedPaymentMethod = ref('ALIPAY')
const qrPreviewVisible = ref(false)
const emailCopied = ref(false)
const acceptedAgreementKeys = ref([])
const activeAgreementIndex = ref(0)
const cancelMessage = ref('')
const revisingOrderId = ref('')
const nowTick = ref(Date.now())
let receiptCountdownTimer = null
let paymentPollTimer = null
let paymentPollAttempts = 0
let paymentResumeCheckAt = 0
let chinaAreaLoadingPromise = null
let draftSavingEnabled = true
let checkoutIdempotencyKey = ''
const LAST_PAY_ORDER_ID_KEY = 'dzzj_website_last_pay_order_id'
const ORDER_DRAFT_KEY = 'dzzj_website_order_draft'
const PAYMENT_OPENED_KEY = 'dzzj_website_payment_opened'
const ORDER_IDEMPOTENCY_KEY = 'dzzj_website_order_idempotency_key'
const productConfig = ref({
  orderEnabled: false,
  priceHidden: true,
  productCode: '',
  productName: '',
  productType: '',
  unitPrice: '',
  freightAmount: '',
  giftComputePower: '',
  servicePeriod: '',
  includesText: '',
  agreements: [],
  bankTransferEnabled: false,
  bankName: '',
  bankAccountName: '',
  bankAccountNumber: '',
  bankBranchName: '',
  bankTransferInstructions: '',
  customerServiceQrCode: '',
  email: company.email
})
const productConfigLoaded = ref(false)
const productConfigLoading = ref(true)
const productConfigError = ref('')
const provinceOptions = ref([])
const orderForm = ref({
  addressId: '',
  userPhone: localStorage.getItem('dzzj_website_phone') || '',
  userNickname: '',
  quantity: 1,
  receiverName: '',
  receiverPhone: '',
  province: '',
  city: '',
  district: '',
  addressDetail: '',
  serviceContact: '',
  servicePhone: '',
  remark: ''
})

const pageMeta = {
  about: {
    kicker: 'Company',
    title: '维态智科',
    // desc: '更聚焦、更清晰地介绍维态科技的定位、产品和能力。'
    desc: '以 AI 连接用户参与、企业增长与价值共享的数字生态共建者。'
  },
  culture: {
    kicker: 'Culture & Vision',
    title: cultureContent.title,
  },
  'order-form': {
    kicker: 'Step 01',
    title: '填写订单表单',
    desc: '填写实体产品收货信息与软件服务联系人。'
  },
  'order-agreement': {
    kicker: 'Step 02',
    title: '确认订购协议',
    desc: '阅读并确认 VitaeAI 智能体数字员工系统订购协议。'
  },
  'order-pay': {
    kicker: 'Step 03',
    title: '确认订单与支付',
    desc: '确认订单金额和交付信息后完成线上支付。'
  },
  orders: {
    kicker: 'Orders',
    title: '我的订单',
    desc: '用户可查看官网订单、支付状态、审核状态和交付进度。'
  },
  'order-detail': {
    kicker: 'Order Detail',
    title: '订单详情',
    desc: '展示实体产品物流、软件服务开通和确认收货入口。'
  },
  'user-center': {
    kicker: 'Account',
    title: '用户中心',
    desc: '展示线上用户信息、订单入口和收货地址信息。'
  },
  address: {
    kicker: 'Addresses',
    title: '收货地址',
    desc: '管理实体产品配送地址。'
  },
  register: {
    kicker: 'Registration',
    title: '注册账号',
    desc: '创建点指之间账号，注册成功后引导下载 APP。'
  },
  download: {
    kicker: 'App Release',
    title: 'APP 下载',
    desc: '查看官方发布渠道、版本信息并获取手机下载二维码。'
  }
}

const currentPage = computed(() => pageMeta[activeView.value] || pageMeta.about)
const routeTable = [
  { path: '/', view: 'home' },
  { path: '/register', view: 'register' },
  { path: '/download', view: 'download' },
  { path: '/about', view: 'about' },
  { path: '/culture', view: 'culture' },
  { path: '/order/form', view: 'order-form' },
  { path: '/order/agreement', view: 'order-agreement' },
  { path: '/order/pay', view: 'order-pay' },
  { path: '/user', view: 'user-center' },
  { path: '/user/orders', view: 'orders' },
  { path: '/user/address', view: 'address' }
]
const tradeViews = ['order-form', 'order-agreement', 'order-pay', 'orders', 'order-detail', 'user-center', 'address']
const isTradeView = computed(() => tradeViews.includes(activeView.value))
const isLoggedIn = computed(() => Boolean(authToken.value))
const isRevisingOrder = computed(() => Boolean(revisingOrderId.value))
const canCreateOrder = computed(() => productConfigLoaded.value && productConfig.value.orderEnabled === true)
const shouldHidePrice = computed(() => productConfig.value.priceHidden === true)
const orderClosedMessage = computed(() => {
  if (canCreateOrder.value) return ''
  if (!productConfigLoaded.value) return '官网下单配置暂未加载成功，当前无法创建新订单。'
  return '官网下单暂未开放，请稍后再试。'
})
const userProfile = computed(() => ({
  name: orderForm.value.userNickname || orderForm.value.receiverName || '官网用户',
  phone: orderForm.value.userPhone
}))
const pendingPayCount = computed(() => orders.value.filter((order) => canContinuePay(order)).length)
const finishedCount = computed(() => orders.value.filter((order) => order.orderStatus === 6).length)
const defaultAddressText = computed(() => addresses.value[0] ? fullAddress(addresses.value[0]) : '暂无收货地址')
const latestServiceContact = computed(() => {
  const order = orders.value[0] || currentOrder.value
  return order?.serviceContact ? `${order.serviceContact} · ${order.servicePhone || ''}` : '暂无服务联系人'
})
const productIncludes = computed(() => splitLines(productConfig.value.includesText))
const productSummary = computed(() => [
  productConfig.value.productType,
  productConfig.value.servicePeriod,
  productConfig.value.giftComputePower
].filter(Boolean).join(' · '))
const websiteProducts = computed(() => products.map((product) => {
  if (product.key !== 'employee') return product
  return {
    ...product,
    title: productConfig.value.productName || '官方商品信息',
    subtitle: productSummary.value || '官方配置商品',
    desc: productSummary.value || '官方商品配置已加载。',
    points: productIncludes.value,
    workflow: [],
    metrics: [
      { label: '商品形态', value: productConfig.value.productType },
      { label: '服务周期', value: productConfig.value.servicePeriod },
      { label: '赠送资源', value: productConfig.value.giftComputePower }
    ].filter(metric => metric.value),
    delivery: [
      productConfig.value.productType,
      productConfig.value.servicePeriod
    ].filter(Boolean).join(' · ')
  }
}))
const orderAgreements = computed(() => normalizeAgreements(productConfig.value))
const activeAgreement = computed(() => orderAgreements.value[activeAgreementIndex.value] || orderAgreements.value[0] || null)
const allAgreementsAccepted = computed(() => {
  const accepted = new Set(acceptedAgreementKeys.value)
  return orderAgreements.value.length > 0 && orderAgreements.value.every((agreement) => accepted.has(agreement.key))
})
const agreementButtonText = computed(() => allAgreementsAccepted.value ? '同意全部协议' : '请先勾选全部协议')
const contactEmail = computed(() => productConfig.value.email || company.email)
const emailCopyText = computed(() => emailCopied.value ? '已复制' : '点击复制')
const orderQuantity = computed(() => Math.max(1, Math.floor(Number(orderForm.value.quantity || 1))))
const cityOptions = computed(() => {
  const province = provinceOptions.value.find((item) => item.value === orderForm.value.province)
  return province?.children || []
})
const districtOptions = computed(() => {
  const city = cityOptions.value.find((item) => item.value === orderForm.value.city)
  return city?.children || []
})
const orderTotalLabel = computed(() => {
  const unitPrice = Number(productConfig.value.unitPrice || 0)
  const freightAmount = Number(productConfig.value.freightAmount || 0)
  return displayMoney(unitPrice * orderQuantity.value + freightAmount)
})
const paymentTerminal = computed(() => isMobileDevice() ? 'MOBILE' : 'WEB')
const bankTransferAvailable = computed(() => {
  const config = productConfig.value
  return config.bankTransferEnabled === true
    && Boolean(config.bankName && config.bankAccountName && config.bankAccountNumber)
})
const bankAccountDetails = computed(() => {
  const snapshot = parseBankAccountSnapshot(currentOrder.value?.bankTransferAccountSnapshot)
  return snapshot || {
    bankName: productConfig.value.bankName,
    bankAccountName: productConfig.value.bankAccountName,
    bankAccountNumber: productConfig.value.bankAccountNumber,
    bankBranchName: productConfig.value.bankBranchName,
    bankTransferInstructions: productConfig.value.bankTransferInstructions
  }
})
const bankTransferPending = computed(() => toNumber(currentOrder.value?.bankTransferStatus) === 1)
const paymentSettlementPending = computed(() => canContinuePay(currentOrder.value) || bankTransferPending.value)
const showBankAccountDetails = computed(() => {
  if (!currentOrder.value) return false
  return selectedPaymentMethod.value === 'BANK_TRANSFER'
    && (bankTransferAvailable.value || toNumber(currentOrder.value.bankTransferStatus) > 0)
})
const paymentTitle = computed(() => {
  if (!currentOrder.value) return '确认订单与支付'
  if (bankTransferPending.value) return '转账待核对'
  if (canReviseOrder(currentOrder.value)) return '订单需要修改'
  if (toNumber(currentOrder.value.refundStatus) === 2) return '订单已退款'
  if (toNumber(currentOrder.value.orderStatus) === 7) return '订单已关闭'
  if (toNumber(currentOrder.value.payStatus) === 1) return '支付成功'
  return '确认订单与支付'
})
const paymentHint = computed(() => {
  const order = currentOrder.value
  if (!order) return '正在读取当前订单状态。'
  if (toNumber(order.bankTransferStatus) === 1) return '转账信息已提交，后台人员核对到账后订单将自动进入审核。'
  if (toNumber(order.bankTransferStatus) === 3) {
    return order.bankTransferReviewRemark
      ? `上次转账核对未通过：${order.bankTransferReviewRemark}`
      : '上次转账核对未通过，请确认到账后重新提交。'
  }
  if (canContinuePay(order)) {
    if (selectedPaymentMethod.value === 'BANK_TRANSFER') return '请按下方账户信息完成转账，并在转账附言中填写订单号。'
    return paymentTerminal.value === 'MOBILE'
      ? '核对订单金额后将自动打开支付宝完成支付。'
      : '核对订单金额后将打开支付宝网页扫码支付。'
  }
  if (canReviseOrder(order)) return '后台审核认为订单信息需要修改，请按审核说明调整后重新提交。'
  if (toNumber(order.payStatus) === 1 && toNumber(order.orderStatus) === 2) return '订单已支付，等待后台审核中。'
  if (toNumber(order.orderStatus) === 7) return closedOrderHint(order)
  if (toNumber(order.payStatus) === 1) return receiptActionText(order)
  return '当前订单状态不能继续支付。'
})
const paymentButtonText = computed(() => paymentTerminal.value === 'MOBILE' ? '打开支付宝支付' : '支付宝扫码支付')
const paymentExpireAt = computed(() => parseDateTime(currentOrder.value?.paymentExpireTime))
const paymentExpired = computed(() => paymentExpireAt.value > 0 && paymentExpireAt.value <= nowTick.value)
const paymentCountdownText = computed(() => {
  if (!paymentExpireAt.value) return '请尽快完成支付'
  if (paymentExpired.value) return '支付时间已截止，请重新检查订单状态'
  return `${formatPaymentRemaining(paymentExpireAt.value - nowTick.value)} 后截止`
})
const mobileCheckoutBarVisible = computed(() => ['order-form', 'order-agreement', 'order-pay'].includes(activeView.value))
const mobileCheckoutAmount = computed(() => {
  if (activeView.value === 'order-pay' && currentOrder.value) {
    return displayMoney(currentOrder.value.payAmount || currentOrder.value.totalAmount)
  }
  return orderTotalLabel.value
})
const mobileCheckoutMeta = computed(() => {
  if (activeView.value === 'order-form') return `数量 ${orderQuantity.value}`
  if (activeView.value === 'order-agreement') return `已确认 ${acceptedAgreementKeys.value.length}/${orderAgreements.value.length}`
  if (bankTransferPending.value) return '转账待核对'
  if (paymentExpired.value) return '支付已截止'
  return paymentWasOpened.value ? '等待支付结果' : '应付金额'
})
const orderSteps = [
  { label: '01', title: '填写订单表单', text: '填写实体收货与软件服务联系人。' },
  { label: '02', title: '确认协议', text: '阅读并确认订购协议。' },
  { label: '03', title: '确认订单与支付', text: '核对订单后完成线上支付。' }
]
const wechatQrCells = [
  '1 / 1', '1 / 2', '1 / 3', '1 / 6', '1 / 7',
  '2 / 1', '2 / 3', '2 / 5', '2 / 7',
  '3 / 1', '3 / 2', '3 / 3', '3 / 5', '3 / 6',
  '4 / 4', '4 / 6',
  '5 / 1', '5 / 3', '5 / 4', '5 / 7',
  '6 / 2', '6 / 5', '6 / 6',
  '7 / 1', '7 / 2', '7 / 4', '7 / 6', '7 / 7'
]

function pathnameFromPath(path) {
  try {
    return new URL(path, window.location.origin).pathname
  } catch (error) {
    return String(path || '/').split('?')[0] || '/'
  }
}

function pcPath(path) {
  const value = String(path || '/')
  if (/^(https?:|mailto:|#)/i.test(value)) return value
  if (value === '/pc' || value.startsWith('/pc/')) return value
  return `/pc${value.startsWith('/') ? value : `/${value}`}`
}

function getViewFromPath(pathname) {
  let cleanPath = pathnameFromPath(pathname)
  if (cleanPath === '/pc') cleanPath = '/'
  else if (cleanPath.startsWith('/pc/')) cleanPath = cleanPath.slice(3)
  if (cleanPath.length > 1) cleanPath = cleanPath.replace(/\/+$/, '')
  if (cleanPath === '/login') {
    return 'login-gate'
  }
  if (cleanPath.startsWith('/user/orders/') && cleanPath !== '/user/orders') {
    if (!isLoggedIn.value) {
      loginReturnPath.value = cleanPath
      return 'login-gate'
    }
    return 'order-detail'
  }
  const route = routeTable.find((item) => item.path === cleanPath)
  const view = route?.view || 'home'
  if (tradeViews.includes(view) && !isLoggedIn.value) {
    loginReturnPath.value = cleanPath
    return 'login-gate'
  }
  return view
}

function setActivePath(pathname, behavior = 'smooth') {
  activeView.value = getViewFromPath(pathname)
  window.scrollTo({ top: 0, behavior })
  loadTradeDataForView(pathname)
}

function goToPath(path) {
  const targetPath = pcPath(path)
  const currentPath = `${window.location.pathname}${window.location.search}`
  if (currentPath !== targetPath) {
    window.history.pushState(null, '', targetPath)
  }
  setActivePath(targetPath)
}

function openProtectedPath(path) {
  if (isLoggedIn.value) {
    goToPath(path)
    return
  }
  loginReturnPath.value = pathnameFromPath(path)
  goToPath(path)
}

function openUserCenter() {
  openProtectedPath('/user')
}

function paymentPathForOrder(order) {
  return order?.id ? `/order/pay?orderId=${encodeURIComponent(order.id)}` : '/order/pay'
}

function rememberPayOrder(order) {
  if (order?.id) {
    localStorage.setItem(LAST_PAY_ORDER_ID_KEY, String(order.id))
  }
}

function forgetPayOrder(order) {
  if (!order?.id) return
  if (localStorage.getItem(LAST_PAY_ORDER_ID_KEY) === String(order.id)) {
    localStorage.removeItem(LAST_PAY_ORDER_ID_KEY)
  }
}

function getPayOrderIdFromLocation() {
  const orderId = normalizeOrderId(new URLSearchParams(window.location.search).get('orderId'))
  return orderId || normalizeOrderId(localStorage.getItem(LAST_PAY_ORDER_ID_KEY))
}

function normalizeOrderId(value) {
  const segments = String(value || '').split(/[/?&]/).filter(Boolean)
  const candidate = segments[segments.length - 1] || ''
  return /^\d+$/.test(candidate) ? candidate : ''
}

async function loadChinaAreaData() {
  if (provinceOptions.value.length > 0) return provinceOptions.value
  if (!chinaAreaLoadingPromise) {
    chinaAreaLoadingPromise = import('./data/chinaArea')
      .then((module) => {
        provinceOptions.value = module.chinaAreaOptions || []
        return provinceOptions.value
      })
      .catch(() => {
        chinaAreaLoadingPromise = null
        if (activeView.value === 'order-form') {
          tradeMessage.value = '地区数据加载失败，请刷新后重试。'
        }
        return []
      })
  }
  return chinaAreaLoadingPromise
}

function restoreOrderDraft() {
  try {
    const rawDraft = sessionStorage.getItem(ORDER_DRAFT_KEY)
    if (!rawDraft) return
    const draft = JSON.parse(rawDraft)
    const restoredForm = {}
    Object.keys(orderForm.value).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(draft.form || {}, key)) {
        restoredForm[key] = draft.form[key]
      }
    })
    orderForm.value = { ...orderForm.value, ...restoredForm }
    selectedAddressId.value = draft.selectedAddressId || ''
    sameServiceContact.value = draft.sameServiceContact !== false
    acceptedAgreementKeys.value = Array.isArray(draft.acceptedAgreementKeys) ? draft.acceptedAgreementKeys : []
    activeAgreementIndex.value = Number.isInteger(draft.activeAgreementIndex) ? draft.activeAgreementIndex : 0
    syncServiceContact()
  } catch (error) {
    sessionStorage.removeItem(ORDER_DRAFT_KEY)
  }
}

function saveOrderDraft() {
  if (!draftSavingEnabled) return
  try {
    sessionStorage.setItem(ORDER_DRAFT_KEY, JSON.stringify({
      form: orderForm.value,
      selectedAddressId: selectedAddressId.value,
      sameServiceContact: sameServiceContact.value,
      acceptedAgreementKeys: acceptedAgreementKeys.value,
      activeAgreementIndex: activeAgreementIndex.value
    }))
  } catch (error) {
    // A draft is a convenience; checkout remains usable if storage is unavailable.
  }
}

function clearOrderDraft() {
  draftSavingEnabled = false
  sessionStorage.removeItem(ORDER_DRAFT_KEY)
}

function createIdempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`
}

function getOrderIdempotencyKey() {
  if (checkoutIdempotencyKey) return checkoutIdempotencyKey
  try {
    checkoutIdempotencyKey = sessionStorage.getItem(ORDER_IDEMPOTENCY_KEY) || ''
  } catch (error) {
    checkoutIdempotencyKey = ''
  }
  if (!checkoutIdempotencyKey) {
    checkoutIdempotencyKey = createIdempotencyKey()
    try {
      sessionStorage.setItem(ORDER_IDEMPOTENCY_KEY, checkoutIdempotencyKey)
    } catch (error) {
      // The in-memory key still protects retries in the current page session.
    }
  }
  return checkoutIdempotencyKey
}

function clearOrderIdempotencyKey() {
  checkoutIdempotencyKey = ''
  try {
    sessionStorage.removeItem(ORDER_IDEMPOTENCY_KEY)
  } catch (error) {
    // Storage may be unavailable in privacy-restricted browsers.
  }
}

function syncServiceContact() {
  if (!sameServiceContact.value) return
  orderForm.value.serviceContact = orderForm.value.receiverName || ''
  orderForm.value.servicePhone = orderForm.value.receiverPhone || ''
  clearOrderError('serviceContact')
  clearOrderError('servicePhone')
}

function normalizeQuantity() {
  const value = Math.floor(Number(orderForm.value.quantity))
  orderForm.value.quantity = Number.isFinite(value) && value >= 1 ? value : 1
}

function changeQuantity(delta) {
  if (isRevisingOrder.value) return
  normalizeQuantity()
  orderForm.value.quantity = Math.max(1, orderForm.value.quantity + delta)
}

function clearOrderError(field) {
  if (!orderErrors.value[field]) return
  const nextErrors = { ...orderErrors.value }
  delete nextErrors[field]
  orderErrors.value = nextErrors
}

function clearOrderErrors() {
  orderErrors.value = {}
}

function validateOrderForm() {
  const form = orderForm.value
  const requiredFields = [
    ['receiverName', '请填写收货人'],
    ['receiverPhone', '请填写联系电话'],
    ['province', '请选择省份'],
    ['city', '请选择城市'],
    ['district', '请选择区县'],
    ['addressDetail', '请填写详细地址'],
    ['serviceContact', '请填写软件服务联系人'],
    ['servicePhone', '请填写服务联系电话']
  ]
  const errors = {}
  requiredFields.forEach(([field, message]) => {
    if (!String(form[field] || '').trim()) errors[field] = message
  })
  if (form.receiverPhone && !/^1[3-9]\d{9}$/.test(String(form.receiverPhone).trim())) {
    errors.receiverPhone = '请输入正确的 11 位手机号'
  }
  if (form.servicePhone && !/^1[3-9]\d{9}$/.test(String(form.servicePhone).trim())) {
    errors.servicePhone = '请输入正确的 11 位手机号'
  }
  orderErrors.value = errors
  return Object.keys(errors).length === 0
}

async function focusFirstOrderError() {
  await nextTick()
  const field = Object.keys(orderErrors.value)[0]
  if (!field) return
  const input = document.querySelector(`[data-order-field="${field}"]`)
  input?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  window.setTimeout(() => input?.focus({ preventScroll: true }), 280)
}

function maybeApplyDefaultAddress() {
  if (activeView.value !== 'order-form' || selectedAddressId.value || addresses.value.length === 0) return
  if (orderForm.value.receiverName || orderForm.value.province || orderForm.value.addressDetail) return
  const address = addresses.value.find((item) => Number(item.defaultFlag) === 1) || addresses.value[0]
  if (!address?.id) return
  selectedAddressId.value = String(address.id)
  applySelectedAddress()
}

function startOrder() {
  if (!canCreateOrder.value) {
    tradeMessage.value = orderClosedMessage.value
    return
  }
  draftSavingEnabled = true
  loadChinaAreaData()
  openProtectedPath('/order/form')
}

function showDevelopingDialog() {
  developingDialogVisible.value = true
}

function openQrPreview() {
  qrPreviewVisible.value = true
}

async function copyContactEmail() {
  const email = contactEmail.value
  if (!email) return
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(email)
    } else {
      const input = document.createElement('input')
      input.value = email
      input.setAttribute('readonly', 'readonly')
      input.style.position = 'fixed'
      input.style.left = '-9999px'
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
    }
    emailCopied.value = true
    window.setTimeout(() => {
      emailCopied.value = false
    }, 1800)
  } catch (error) {
    tradeMessage.value = '邮箱复制失败，请手动选择复制。'
  }
}

function clearLoginState() {
  authToken.value = ''
  localStorage.removeItem('dzzj_website_token')
  localStorage.removeItem('dzzj_website_refresh_token')
  localStorage.removeItem('dzzj_website_phone')
  localStorage.removeItem(LAST_PAY_ORDER_ID_KEY)
  orderForm.value.userPhone = ''
  loginForm.value.password = ''
  orders.value = []
  addresses.value = []
  currentOrder.value = null
}

function isValidLoginPhone() {
  return /^1[3-9]\d{9}$/.test(loginForm.value.phone)
}

function logout() {
  clearLoginState()
  goToPath('/login')
}

async function submitLogin() {
  if (!isValidLoginPhone()) {
    loginMessage.value = '请输入正确的 11 位手机号。'
    return
  }
  if (!loginForm.value.password) {
    loginMessage.value = '请输入密码。'
    return
  }
  loginLoading.value = true
  loginMessage.value = ''
  try {
    const token = await loginWithPassword(loginForm.value.phone, loginForm.value.password)
    authToken.value = token.access_token
    localStorage.setItem('dzzj_website_token', authToken.value)
    const refreshToken = token.refresh_token || token.refreshToken
    if (refreshToken) {
      localStorage.setItem('dzzj_website_refresh_token', refreshToken)
    }
    localStorage.setItem('dzzj_website_phone', loginForm.value.phone)
    orderForm.value.userPhone = loginForm.value.phone
    if (!orderForm.value.receiverPhone) {
      orderForm.value.receiverPhone = loginForm.value.phone
    }
    syncServiceContact()
    const targetPath = loginReturnPath.value || '/order/form'
    loginReturnPath.value = '/order/form'
    goToPath(targetPath)
  } catch (error) {
    loginMessage.value = error.message || '登录失败'
  } finally {
    loginLoading.value = false
  }
}

async function submitOrderForm() {
  if (!isRevisingOrder.value && !canCreateOrder.value) {
    tradeMessage.value = orderClosedMessage.value
    return
  }
  syncServiceContact()
  normalizeQuantity()
  if (!validateOrderForm()) {
    tradeMessage.value = '请检查表单中标注的信息。'
    await focusFirstOrderError()
    return
  }
  orderForm.value.userPhone = loginForm.value.phone || orderForm.value.userPhone
  orderForm.value.userNickname = orderForm.value.receiverName || orderForm.value.userNickname
  orderForm.value.quantity = orderQuantity.value
  orderForm.value.addressId = selectedAddressId.value || ''
  tradeMessage.value = ''
  activeAgreementIndex.value = 0
  if (isRevisingOrder.value) {
    await submitOrderRevision()
    return
  }
  goToPath('/order/agreement')
}

function selectAgreement(index) {
  activeAgreementIndex.value = index
}

async function submitAgreement() {
  if (!allAgreementsAccepted.value) {
    tradeMessage.value = '请阅读并勾选全部协议后继续。'
    return
  }
  const unpaidOrder = findUnpaidOrder()
  if (unpaidOrder) {
    clearOrderIdempotencyKey()
    currentOrder.value = unpaidOrder
    rememberPayOrder(unpaidOrder)
    goToPath(paymentPathForOrder(unpaidOrder))
    tradeMessage.value = '已有待支付订单，请继续完成支付。'
    return
  }
  if (!canCreateOrder.value) {
    tradeMessage.value = orderClosedMessage.value
    return
  }
  await runTradeAction(async () => {
    orderForm.value.quantity = orderQuantity.value
    orderForm.value.addressId = selectedAddressId.value || ''
    const order = await createWebsiteOrder(orderForm.value, getOrderIdempotencyKey())
    clearOrderIdempotencyKey()
    currentOrder.value = order
    orders.value = [order, ...orders.value.filter((item) => item.id !== order.id)]
    await refreshAddresses()
    rememberPayOrder(order)
    acceptedAgreementKeys.value = []
    activeAgreementIndex.value = 0
    clearOrderDraft()
    goToPath(paymentPathForOrder(order))
  }, '协议已确认，订单已创建，请完成支付。')
}

async function submitOrderRevision() {
  await runTradeAction(async () => {
    const order = await reviseWebsiteOrder(revisingOrderId.value, orderForm.value)
    currentOrder.value = order
    revisingOrderId.value = ''
    await refreshOrders()
    await refreshAddresses()
    acceptedAgreementKeys.value = []
    activeAgreementIndex.value = 0
    clearOrderDraft()
    goToPath(`/user/orders/${order.id}`)
  }, '订单信息已重新提交，等待后台审核。')
}

async function submitPayment() {
  await ensureCurrentOrder()
  if (!currentOrder.value) return
  await reloadCurrentOrder()
  if (!canContinuePay(currentOrder.value)) {
    tradeMessage.value = paymentHint.value
    return
  }
  await runTradeAction(async () => {
    const pay = await createWebsiteAlipayPay(currentOrder.value.id, paymentTerminal.value)
    markPaymentOpened(currentOrder.value)
    startPaymentPolling()
    openAlipayPay(pay)
  }, paymentTerminal.value === 'MOBILE' ? '正在跳转支付宝。' : '请使用支付宝扫描二维码完成支付。')
}

function selectPaymentMethod(method) {
  if (paymentWasOpened.value || !['ALIPAY', 'BANK_TRANSFER'].includes(method)) return
  if (method === 'BANK_TRANSFER' && !bankTransferAvailable.value) return
  selectedPaymentMethod.value = method
  tradeMessage.value = ''
}

function openBankTransferConfirm() {
  if (!canContinuePay(currentOrder.value) || !bankTransferAvailable.value || paymentExpired.value) return
  bankTransferConfirmVisible.value = true
}

async function submitBankTransfer() {
  if (!currentOrder.value) return
  tradeLoading.value = true
  tradeMessage.value = ''
  try {
    currentOrder.value = await submitWebsiteBankTransfer(currentOrder.value.id, paymentTerminal.value)
    orders.value = [currentOrder.value, ...orders.value.filter((item) => `${item.id}` !== `${currentOrder.value.id}`)]
    bankTransferConfirmVisible.value = false
    rememberPayOrder(currentOrder.value)
    syncPaymentOpenedState(currentOrder.value)
    tradeMessage.value = '转账信息已提交，正在等待后台核对到账。'
  } catch (error) {
    tradeMessage.value = error.message || '转账核对提交失败，请稍后重试。'
  } finally {
    tradeLoading.value = false
  }
}

async function copyBankAccount(value) {
  if (!value) return
  try {
    await navigator.clipboard.writeText(String(value))
    tradeMessage.value = '银行账号已复制。'
  } catch (error) {
    tradeMessage.value = '复制失败，请长按银行账号复制。'
  }
}

async function copyOrderNo(value) {
  if (!value) return
  try {
    await navigator.clipboard.writeText(String(value))
    tradeMessage.value = '订单号已复制，可粘贴到转账附言。'
  } catch (error) {
    tradeMessage.value = '复制失败，请长按订单号复制。'
  }
}

function markPaymentOpened(order) {
  if (!order?.id) return
  paymentWasOpened.value = true
  sessionStorage.setItem(PAYMENT_OPENED_KEY, String(order.id))
}

function syncPaymentOpenedState(order) {
  const orderId = order?.id ? String(order.id) : ''
  paymentWasOpened.value = Boolean(orderId && sessionStorage.getItem(PAYMENT_OPENED_KEY) === orderId)
  if (order && !canContinuePay(order)) {
    sessionStorage.removeItem(PAYMENT_OPENED_KEY)
    paymentWasOpened.value = false
    stopPaymentPolling()
  }
}

function stopPaymentPolling() {
  if (paymentPollTimer) {
    window.clearInterval(paymentPollTimer)
    paymentPollTimer = null
  }
  paymentPollAttempts = 0
}

function startPaymentPolling() {
  stopPaymentPolling()
  paymentPollTimer = window.setInterval(async () => {
    paymentPollAttempts += 1
    await checkPaymentStatus(false)
    if (!canContinuePay(currentOrder.value) || paymentPollAttempts >= 8) {
      stopPaymentPolling()
    }
  }, 2500)
}

async function checkPaymentStatus(announce = true) {
  if (paymentCheckLoading.value) return
  const orderId = currentOrder.value?.id || getPayOrderIdFromLocation()
  if (!orderId) {
    if (announce === true) tradeMessage.value = '暂未找到需要查询的订单。'
    return
  }
  paymentCheckLoading.value = true
  try {
    const order = await getWebsiteOrder(orderId)
    currentOrder.value = order
    orders.value = [order, ...orders.value.filter((item) => `${item.id}` !== `${order.id}`)]
    if (toNumber(order.payStatus) === 1) {
      forgetPayOrder(order)
      sessionStorage.removeItem(PAYMENT_OPENED_KEY)
      paymentWasOpened.value = false
      stopPaymentPolling()
      tradeMessage.value = '支付已确认，订单已进入后台审核。'
    } else if (!canContinuePay(order)) {
      syncPaymentOpenedState(order)
      tradeMessage.value = paymentHint.value
    } else if (announce === true) {
      tradeMessage.value = '暂未查询到支付结果。如支付宝已扣款，请稍候几秒后重新检查。'
    }
  } catch (error) {
    if (announce === true) {
      tradeMessage.value = error.message || '支付结果查询失败，请稍后重试。'
    }
  } finally {
    paymentCheckLoading.value = false
  }
}

function handlePaymentReturn() {
  if (activeView.value !== 'order-pay' || !paymentWasOpened.value || !canContinuePay(currentOrder.value)) return
  const now = Date.now()
  if (now - paymentResumeCheckAt < 1200) return
  paymentResumeCheckAt = now
  checkPaymentStatus(false)
  startPaymentPolling()
}

function onPageShow() {
  handlePaymentReturn()
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') handlePaymentReturn()
}

function onWindowFocus() {
  handlePaymentReturn()
}

function shouldHideMobileCheckoutBar(target) {
  if (window.innerWidth > 640 || !target || target.disabled || target.readOnly) return false
  if (target.matches?.('textarea')) return true
  if (!target.matches?.('input')) return false
  return ['text', 'search', 'tel', 'url', 'email', 'password', 'number'].includes(target.type || 'text')
}

function onDocumentFocusIn(event) {
  mobileKeyboardOpen.value = shouldHideMobileCheckoutBar(event.target)
}

function onDocumentFocusOut() {
  window.setTimeout(() => {
    mobileKeyboardOpen.value = shouldHideMobileCheckoutBar(document.activeElement)
  })
}

async function submitReceipt() {
  if (!currentOrder.value) return
  receiptConfirmVisible.value = false
  await runTradeAction(async () => {
    await confirmWebsiteReceipt(currentOrder.value.id)
    await refreshOrders()
    await reloadCurrentOrder()
  }, '确认收货申请已提交，48小时内人工审核。')
}

async function extendReceipt() {
  if (!currentOrder.value || !canExtendReceipt(currentOrder.value)) return
  await runTradeAction(async () => {
    await extendWebsiteReceipt(currentOrder.value.id)
    await refreshOrders()
    await reloadCurrentOrder()
  }, '已延长收货 48 小时。')
}

function openReceiptConfirm() {
  if (!canConfirmReceipt(currentOrder.value)) return
  receiptConfirmVisible.value = true
}

function openCancelConfirm() {
  if (!canCancelOrder(currentOrder.value)) return
  cancelMessage.value = ''
  cancelConfirmVisible.value = true
}

async function submitCancelOrder() {
  if (!currentOrder.value) return
  tradeLoading.value = true
  cancelMessage.value = ''
  tradeMessage.value = ''
  try {
    await cancelWebsiteOrder(currentOrder.value.id)
    await refreshOrders()
    await reloadCurrentOrder()
    forgetPayOrder(currentOrder.value)
    cancelConfirmVisible.value = false
    tradeMessage.value = cancelSuccessText(currentOrder.value)
  } catch (error) {
    cancelMessage.value = error.message || '取消订单失败，请稍后重试'
    tradeMessage.value = cancelMessage.value
  } finally {
    tradeLoading.value = false
  }
}

async function openOrderDetail(order) {
  currentOrder.value = order
  goToPath(`/user/orders/${order.id}`)
}

function continueOrderPayment(order) {
  if (!canContinuePay(order)) return
  currentOrder.value = order
  rememberPayOrder(order)
  goToPath(paymentPathForOrder(order))
}

function startOrderRevision(order) {
  if (!canReviseOrder(order)) return
  draftSavingEnabled = true
  currentOrder.value = order
  revisingOrderId.value = order.id
  selectedAddressId.value = ''
  orderForm.value = {
    ...orderForm.value,
    addressId: '',
    userPhone: order.userPhone || orderForm.value.userPhone,
    userNickname: order.userNickname || orderForm.value.userNickname,
    quantity: order.quantity || 1,
    receiverName: order.receiverName || '',
    receiverPhone: order.receiverPhone || '',
    province: order.province || '',
    city: order.city || '',
    district: order.district || '',
    addressDetail: order.addressDetail || '',
    serviceContact: order.serviceContact || '',
    servicePhone: order.servicePhone || '',
    remark: order.remark || ''
  }
  sameServiceContact.value = orderForm.value.receiverName === orderForm.value.serviceContact
    && orderForm.value.receiverPhone === orderForm.value.servicePhone
  goToPath('/order/form')
}

function applySelectedAddress() {
  const address = addresses.value.find((item) => `${item.id}` === `${selectedAddressId.value}`)
  if (!address) {
    orderForm.value.addressId = ''
    return
  }
  orderForm.value.addressId = address.id
  orderForm.value.receiverName = address.receiverName || ''
  orderForm.value.receiverPhone = address.receiverPhone || ''
  orderForm.value.province = address.province || ''
  orderForm.value.city = address.city || ''
  orderForm.value.district = address.district || ''
  orderForm.value.addressDetail = address.addressDetail || ''
  syncServiceContact()
  clearOrderErrors()
}

function handleProvinceChange() {
  clearOrderError('province')
  orderForm.value.city = ''
  orderForm.value.district = ''
  selectedAddressId.value = ''
  orderForm.value.addressId = ''
}

function handleCityChange() {
  clearOrderError('city')
  orderForm.value.district = ''
  selectedAddressId.value = ''
  orderForm.value.addressId = ''
}

function handleDistrictChange() {
  clearOrderError('district')
  selectedAddressId.value = ''
  orderForm.value.addressId = ''
}

function openAlipayPay(pay) {
  if (!pay?.payForm) {
    throw new Error('支付宝支付表单为空')
  }
  if (pay.terminal === 'WEB') {
    openAlipayQrWindow(pay.payForm)
    return
  }
  openMobileAlipayPay(pay)
}

function openMobileAlipayPay(pay) {
  if (!pay.payUrl) {
    submitAlipayForm(pay.payForm)
    return
  }
  const fallbackUrl = pay.payUrl
  window.location.href = `alipays://platformapi/startapp?appId=20000067&url=${encodeURIComponent(fallbackUrl)}`
  window.setTimeout(() => {
    if (document.visibilityState === 'visible') {
      window.location.href = fallbackUrl
    }
  }, 1200)
}

function openAlipayQrWindow(payForm) {
  const payWindow = window.open('', '_blank')
  if (!payWindow) {
    throw new Error('浏览器已拦截支付页面，请允许弹窗后重试')
  }
  const html = payForm.includes('</body>') ? payForm : `<!doctype html><html><head><meta charset="utf-8"><title>支付宝扫码支付</title></head><body>${payForm}</body></html>`
  payWindow.document.open()
  payWindow.document.write(html)
  payWindow.document.close()
}

function submitAlipayForm(payForm) {
  if (!payForm) {
    throw new Error('支付宝支付表单为空')
  }
  const container = document.createElement('div')
  container.style.display = 'none'
  container.innerHTML = payForm
  document.body.appendChild(container)
  const form = container.querySelector('form')
  if (!form) {
    document.body.removeChild(container)
    throw new Error('支付宝支付表单无效')
  }
  form.submit()
}

async function runTradeAction(action, successMessage) {
  tradeLoading.value = true
  tradeMessage.value = ''
  try {
    await action()
    tradeMessage.value = successMessage
  } catch (error) {
    tradeMessage.value = error.message || '操作失败，请稍后重试'
  } finally {
    tradeLoading.value = false
  }
}

async function loadTradeDataForView(pathname) {
  if (!['user-center', 'orders', 'address', 'order-detail', 'order-pay', 'order-form', 'order-agreement'].includes(activeView.value)) return
  const areaTask = activeView.value === 'order-form' ? loadChinaAreaData() : Promise.resolve()
  await Promise.all([refreshOrders(), refreshAddresses(), areaTask])
  const unpaidOrder = findUnpaidOrder()
  if (!isRevisingOrder.value && unpaidOrder && ['order-form', 'order-agreement'].includes(activeView.value)) {
    clearOrderIdempotencyKey()
    currentOrder.value = unpaidOrder
    rememberPayOrder(unpaidOrder)
    goToPath(paymentPathForOrder(unpaidOrder))
    tradeMessage.value = '协议已确认，当前订单不可返回修改，请继续完成支付。'
    return
  }
  if (activeView.value === 'order-pay') {
    await loadCurrentPaymentOrder()
  }
  if (activeView.value === 'order-detail') {
    const id = normalizeOrderId(pathnameFromPath(pathname).split('/').pop())
    const cached = orders.value.find((order) => `${order.id}` === id)
    currentOrder.value = cached || null
    if (id && !cached) {
      await runTradeAction(async () => {
        currentOrder.value = await getWebsiteOrder(id)
      }, '')
    }
  }
}

async function loadCurrentPaymentOrder() {
  const orderId = getPayOrderIdFromLocation()
  if (orderId) {
    const cached = orders.value.find((order) => `${order.id}` === `${orderId}`)
    if (cached) {
      currentOrder.value = cached
    }
    try {
      currentOrder.value = await getWebsiteOrder(orderId)
      if (shouldRememberPaymentOrder(currentOrder.value)) {
        rememberPayOrder(currentOrder.value)
      } else {
        forgetPayOrder(currentOrder.value)
      }
      syncPaymentOpenedState(currentOrder.value)
      return
    } catch (error) {
      tradeMessage.value = error.message || '订单加载失败'
    }
  }
  await ensureCurrentOrder()
}

async function refreshOrders() {
  try {
    orders.value = await getWebsiteOrders()
  } catch (error) {
    tradeMessage.value = error.message || '订单加载失败'
  }
}

async function refreshAddresses() {
  try {
    addresses.value = await getWebsiteAddresses()
    maybeApplyDefaultAddress()
  } catch (error) {
    tradeMessage.value = error.message || '地址加载失败'
  }
}

async function ensureCurrentOrder() {
  if (currentOrder.value) {
    if (shouldRememberPaymentOrder(currentOrder.value)) {
      rememberPayOrder(currentOrder.value)
    }
    return
  }
  if (orders.value.length === 0) {
    await refreshOrders()
  }
  currentOrder.value = findUnpaidOrder() || null
  if (!currentOrder.value) {
    currentOrder.value = orders.value[0] || null
  }
  if (!currentOrder.value) {
    tradeMessage.value = '请先创建订单'
  } else {
    if (shouldRememberPaymentOrder(currentOrder.value)) {
      rememberPayOrder(currentOrder.value)
    } else {
      forgetPayOrder(currentOrder.value)
    }
    syncPaymentOpenedState(currentOrder.value)
  }
}

function findUnpaidOrder() {
  return orders.value.find((order) => canContinuePay(order))
}

async function reloadCurrentOrder() {
  if (!currentOrder.value) return
  currentOrder.value = await getWebsiteOrder(currentOrder.value.id)
  syncPaymentOpenedState(currentOrder.value)
}

function orderStatusText(status) {
  return {
    0: '待确认协议',
    1: '待支付',
    2: '待审核',
    3: '审核通过待发货',
    4: '已发货',
    5: '收货审核中',
    6: '已收货',
    7: '已关闭'
  }[status] || '未知状态'
}

function orderDisplayStatusText(order) {
  if (canReviseOrder(order)) return '需要修改'
  return orderStatusText(order?.orderStatus)
}

function payStatusText(status) {
  const value = toNumber(status)
  return value === 1 ? '已支付' : '未支付'
}

function orderPayStatusText(order) {
  if (!order) return '未支付'
  if (toNumber(order.bankTransferStatus) === 1) return '转账待核对'
  if (toNumber(order.bankTransferStatus) === 3) return '转账核对未通过'
  return payStatusText(order.payStatus)
}

function bankTransferStatusText(status) {
  return {
    1: '待后台核对',
    2: '核对通过',
    3: '核对未通过'
  }[toNumber(status)] || '未提交'
}

function refundStatusText(status) {
  return {
    0: '无退款',
    1: '待退款',
    2: '退款成功',
    3: '退款失败'
  }[toNumber(status)] || '无退款'
}

function closedOrderHint(order) {
  const refundStatus = toNumber(order?.refundStatus)
  if (refundStatus === 2) return '订单已关闭，退款已处理完成。'
  if (refundStatus === 3) return '订单已关闭，自动退款失败，后台将继续处理手动退款。'
  if (refundStatus === 1) return '订单已关闭，退款需后台人工处理。'
  return '订单已关闭，不能继续支付。'
}

function canContinuePay(order) {
  return !!order
    && toNumber(order.payStatus) === 0
    && toNumber(order.orderStatus) === 1
    && toNumber(order.bankTransferStatus) !== 1
}

function shouldRememberPaymentOrder(order) {
  return canContinuePay(order) || toNumber(order?.bankTransferStatus) === 1
}

function canReviseOrder(order) {
  return !!order && toNumber(order.payStatus) === 1 && toNumber(order.orderStatus) === 2 && toNumber(order.orderAuditStatus) === 3
}

function canCancelOrder(order) {
  if (!order || toNumber(order.orderStatus) === 7 || toNumber(order.orderStatus) >= 3) return false
  if (toNumber(order.bankTransferStatus) === 1) return false
  if (toNumber(order.payStatus) === 0) return toNumber(order.orderStatus) === 1 || toNumber(order.orderStatus) === 0
  return toNumber(order.payStatus) === 1 && toNumber(order.orderStatus) === 2 && [1, 3].includes(toNumber(order.orderAuditStatus))
}

const cancelDialogTitle = computed(() => {
  if (!currentOrder.value || toNumber(currentOrder.value.payStatus) !== 1) return '确认取消该订单？'
  return '确认取消订单并处理退款？'
})

const cancelDialogText = computed(() => {
  const order = currentOrder.value
  if (!order || toNumber(order.payStatus) !== 1) return '订单取消后将关闭，不能继续支付。'
  if (isAlipayOrder(order)) return '支付宝支付订单将先尝试原路自动退款；如果自动退款失败，订单会先关闭，并由后台继续手动退款。'
  return '线下支付订单会先关闭订单，退款由后台人工处理。'
})

function cancelSuccessText(order) {
  if (!order) return '订单已取消。'
  if (toNumber(order.refundStatus) === 2) return '订单已取消，退款已完成。'
  if (toNumber(order.refundStatus) === 3) return '订单已取消，自动退款失败，后台将继续处理手动退款。'
  if (toNumber(order.refundStatus) === 1) return '订单已取消，后台将继续处理人工退款。'
  return '订单已取消。'
}

function isAlipayOrder(order) {
  return String(order?.payMethod || '').toUpperCase() === 'ALIPAY'
}

function parseBankAccountSnapshot(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (error) {
    return null
  }
}

function deliveryStatusText(status) {
  return {
    0: '未发货',
    1: '已发货',
    2: '已收货'
  }[status] || '未发货'
}

function receiptAuditText(status) {
  return {
    0: '未申请',
    1: '人工审核中',
    2: '审核通过',
    3: '审核不通过'
  }[toNumber(status)] || '未申请'
}

function orderAuditText(status) {
  return {
    0: '未提交',
    1: '等待审核中',
    2: '审核通过',
    3: '需要修改',
    4: '直接退款'
  }[toNumber(status)] || '未提交'
}

function canConfirmReceipt(order) {
  if (!order || toNumber(order.payStatus) !== 1 || toNumber(order.deliveryStatus) !== 1 || toNumber(order.orderStatus) >= 6) {
    return false
  }
  if (toNumber(order.receiptAuditStatus) === 1 || toNumber(order.receiptAuditStatus) === 2) {
    return false
  }
  if (toNumber(order.receiptAuditStatus) === 3 && receiptAuditDeadlineAt(order) > nowTick.value) {
    return false
  }
  if (!order.deliveryTime) {
    return false
  }
  return receiptAvailableAt(order) <= nowTick.value
}

function receiptActionText(order) {
  if (!order) return '确认收货'
  if (toNumber(order.orderStatus) === 2 && toNumber(order.orderAuditStatus) === 1) return '等待审核中'
  if (toNumber(order.orderStatus) === 2 && toNumber(order.orderAuditStatus) === 3) return '等待修改后重新提交'
  if (toNumber(order.orderStatus) === 7) return toNumber(order.refundStatus) === 2 ? '订单已退款' : '订单已关闭'
  if (toNumber(order.receiptAuditStatus) === 1) return '订单审核中'
  if (toNumber(order.receiptAuditStatus) === 2) return '已确认收货'
  if (toNumber(order.receiptAuditStatus) === 3 && receiptAuditDeadlineAt(order) > nowTick.value) {
    return `${receiptAuditCountdownText(order)}后可再次确认`
  }
  if (toNumber(order.payStatus) !== 1) return '支付后可确认收货'
  if (toNumber(order.orderAuditStatus) && toNumber(order.orderAuditStatus) !== 2) return '订单审核通过后可确认'
  if (toNumber(order.deliveryStatus) !== 1) return '待发货'
  if (!order.deliveryTime) return '待发货'
  if (!canConfirmReceipt(order)) return `${receiptCountdownText(order)}后可确认收货`
  return '确认收货'
}

function receiptAvailableAt(order) {
  return order?.deliveryTime ? new Date(order.deliveryTime).getTime() + 7 * 24 * 60 * 60 * 1000 : Number.POSITIVE_INFINITY
}

function receiptAuditDeadlineAt(order) {
  return order?.receiptAuditDeadline ? new Date(order.receiptAuditDeadline).getTime() : 0
}

function receiptAuditCountdownText(order) {
  return formatRemainingTime(Math.max(0, receiptAuditDeadlineAt(order) - nowTick.value))
}

function receiptCountdownVisible(order) {
  return Boolean(order && [4, 5].includes(toNumber(order.orderStatus)) && order.deliveryTime)
}

function receiptCountdownLabel(order) {
  return '自动签收倒计时'
}

function autoReceiptDeadlineAt(order) {
  const configuredDeadline = order?.autoReceiptDeadline || order?.receiptAutoConfirmDeadline || order?.receiptAuditDeadline
  if (configuredDeadline) return parseDateTime(configuredDeadline)
  return receiptAvailableAt(order) + 48 * 60 * 60 * 1000
}

function receiptCountdownTextForOrder(order) {
  if (!order) return '-'
  return formatRemainingTime(Math.max(0, autoReceiptDeadlineAt(order) - nowTick.value))
}

function canExtendReceipt(order) {
  const count = toNumber(order?.receiptExtensionCount ?? order?.receiptExtendCount)
  return Boolean(order && toNumber(order.orderStatus) === 5 && toNumber(order.receiptAuditStatus) === 1
    && count < 1 && autoReceiptDeadlineAt(order) > nowTick.value)
}

function receiptCountdownText(order) {
  return formatRemainingTime(Math.max(0, receiptAvailableAt(order) - nowTick.value))
}

function formatRemainingTime(remaining) {
  if (Number(remaining) <= 0) return '0'
  const totalMinutes = Math.ceil(remaining / (60 * 1000))
  const days = Math.floor(totalMinutes / (24 * 60))
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `${days}天 ${String(hours).padStart(2, '0')}小时 ${String(minutes).padStart(2, '0')}分钟`
  if (hours > 0) return `${hours}小时 ${String(minutes).padStart(2, '0')}分钟`
  return `${minutes}分钟`
}

function parseDateTime(value) {
  if (!value) return 0
  const timestamp = new Date(String(value).replace(' ', 'T')).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

function formatPaymentRemaining(remaining) {
  const totalSeconds = Math.max(0, Math.floor(remaining / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return [hours, minutes, seconds].map((item) => String(item).padStart(2, '0')).join(':')
}

function fullAddress(item) {
  return [item?.province, item?.city, item?.district, item?.addressDetail].filter(Boolean).join('')
}

function formatMoney(value) {
  const amount = Number(value || 0)
  return `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function displayMoney(value) {
  return shouldHidePrice.value ? '价格咨询客服' : formatMoney(value)
}

function toNumber(value) {
  return Number(value ?? 0)
}

function splitLines(value) {
  return String(value || '').split(/\r?\n/).map((item) => item.trim()).filter(Boolean)
}

function normalizeAgreements(config) {
  const rawAgreements = firstArray(config?.agreements, config?.agreementList, config?.protocols, config?.protocolList)
  const agreements = rawAgreements.length > 0
    ? rawAgreements
    : [{ title: config?.agreementTitle, content: config?.agreementTemplate }]
  return agreements
    .map((item, index) => {
      const title = String(item?.title || item?.agreementTitle || item?.name || `订购协议 ${index + 1}`).trim()
      const content = item?.content || item?.template || item?.agreementTemplate || item?.text || ''
      return {
        key: String(item?.key || item?.id || `${index}-${title}`),
        label: `协议 ${String(index + 1).padStart(2, '0')}`,
        title,
        html: normalizeAgreementHtml(content),
        plainText: stripHtml(content)
      }
    })
    .filter((item) => item.title && item.plainText.length > 0)
}

function normalizeAgreementHtml(value) {
  const content = String(value || '').trim()
  if (!content) return ''
  const html = /<\/?[a-z][\s\S]*>/i.test(content)
    ? content
    : splitLines(content).map((line) => `<p>${escapeHtml(line)}</p>`).join('')
  return sanitizeAgreementHtml(html)
}

function sanitizeAgreementHtml(html) {
  const template = document.createElement('template')
  template.innerHTML = html
  template.content.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((node) => node.remove())
  template.content.querySelectorAll('*').forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase()
      const value = attribute.value.trim().toLowerCase()
      if (name.startsWith('on') || value.startsWith('javascript:')) {
        node.removeAttribute(attribute.name)
      }
    })
  })
  return template.innerHTML
}

function stripHtml(value) {
  const content = String(value || '')
  if (!/<\/?[a-z][\s\S]*>/i.test(content)) return content.trim()
  const template = document.createElement('template')
  template.innerHTML = content
  return (template.content.textContent || '').trim()
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function firstArray(...values) {
  for (const value of values) {
    if (Array.isArray(value)) return value
    if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value)
        if (Array.isArray(parsed)) return parsed
      } catch (error) {
        // Ignore non-JSON strings; legacy single-agreement fields handle them.
      }
    }
  }
  return []
}

function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
}

function formatTime(value) {
  return value ? String(value).replace('T', ' ') : '待生成'
}

function orderTimeline(order) {
  return [
    { title: '订单已创建', text: '官网创建 VitaeAI 数字员工订单。', time: formatTime(order.createTime) },
    { title: '协议确认', text: '用户确认订购协议后进入支付。', time: formatTime(order.agreementTime) },
    { title: '支付完成', text: '支付后后台安排审核、发货和软件服务开通。', time: formatTime(order.payTime) },
    { title: '确认收货', text: '发货满7天后可申请确认收货，48小时内人工审核。', time: formatTime(order.receiptApplyTime || order.receivedTime) }
  ]
}

async function scrollToProduct(id) {
  activeView.value = 'home'
  if (window.location.pathname !== '/pc') {
    window.history.pushState(null, '', '/pc')
  }
  await nextTick()
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function syncViewFromPath() {
  setActivePath(`${window.location.pathname}${window.location.search}`, 'auto')
}

async function loadProductConfig() {
  productConfigLoaded.value = false
  productConfigLoading.value = true
  productConfigError.value = ''
  try {
    const config = await getWebsiteProductConfig()
    if (config) {
      productConfig.value = { ...productConfig.value, ...config }
      const agreementKeys = new Set(orderAgreements.value.map((agreement) => agreement.key))
      acceptedAgreementKeys.value = acceptedAgreementKeys.value.filter((key) => agreementKeys.has(key))
      activeAgreementIndex.value = Math.min(activeAgreementIndex.value, Math.max(0, orderAgreements.value.length - 1))
      productConfigLoaded.value = true
      return
    }
    tradeMessage.value = '官网下单配置暂未加载成功，当前无法创建新订单。'
  } catch (error) {
    productConfigError.value = error.message || '商品配置加载失败'
    tradeMessage.value = productConfigError.value
  } finally {
    productConfigLoading.value = false
  }
}

function onAuthExpired() {
  const protectedView = tradeViews.includes(activeView.value)
  const currentPath = pathnameFromPath(window.location.pathname)
  const returnPath = currentPath.startsWith('/pc/') ? currentPath.slice(3) : currentPath
  clearLoginState()
  if (!protectedView) return
  loginReturnPath.value = returnPath
  setActivePath(window.location.pathname, 'auto')
}

function onResize() {
  if (window.innerWidth >= 768) {
    mobileMenuOpen.value = false
  }
  if (window.innerWidth > 640) {
    mobileKeyboardOpen.value = false
  }
}

watch(
  () => [orderForm.value.receiverName, orderForm.value.receiverPhone],
  () => syncServiceContact()
)

watch(sameServiceContact, (enabled) => {
  if (enabled) syncServiceContact()
})

watch(
  [orderForm, selectedAddressId, sameServiceContact, acceptedAgreementKeys, activeAgreementIndex],
  () => saveOrderDraft(),
  { deep: true }
)

watch(
  () => [currentOrder.value?.id, currentOrder.value?.payMethod, currentOrder.value?.bankTransferStatus],
  () => {
    syncPaymentOpenedState(currentOrder.value)
    const method = String(currentOrder.value?.payMethod || '').toUpperCase()
    selectedPaymentMethod.value = method === 'BANK_TRANSFER' || toNumber(currentOrder.value?.bankTransferStatus) > 0
      ? 'BANK_TRANSFER'
      : 'ALIPAY'
  }
)

onMounted(() => {
  restoreOrderDraft()
  loadProductConfig()
  syncViewFromPath()
  receiptCountdownTimer = window.setInterval(() => {
    nowTick.value = Date.now()
  }, 1000)
  window.addEventListener('popstate', syncViewFromPath)
  window.addEventListener('resize', onResize)
  window.addEventListener('pageshow', onPageShow)
  window.addEventListener('focus', onWindowFocus)
  window.addEventListener('website-auth-expired', onAuthExpired)
  document.addEventListener('visibilitychange', onVisibilityChange)
  document.addEventListener('focusin', onDocumentFocusIn)
  document.addEventListener('focusout', onDocumentFocusOut)
  if (isLoggedIn.value) {
    refreshOrders()
    refreshAddresses()
  }
})

onBeforeUnmount(() => {
  if (receiptCountdownTimer) {
    window.clearInterval(receiptCountdownTimer)
  }
  stopPaymentPolling()
  window.removeEventListener('popstate', syncViewFromPath)
  window.removeEventListener('resize', onResize)
  window.removeEventListener('pageshow', onPageShow)
  window.removeEventListener('focus', onWindowFocus)
  window.removeEventListener('website-auth-expired', onAuthExpired)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  document.removeEventListener('focusin', onDocumentFocusIn)
  document.removeEventListener('focusout', onDocumentFocusOut)
})
</script>

<style>
.slide-down-enter-active,
.slide-down-leave-active {
  overflow: hidden;
  transition: all 0.25s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
  max-height: 240px;
  opacity: 1;
}
</style>
